// All Database I/O here. This is because I augment the returned data
// from the server to include calculated flags. 
//
// For a Task, I add a bucketName.

import { list, getUrl, remove } from 'aws-amplify/storage';


// TODO: I named my functions create/retrieve/update/delete, which conflict with
// Amplify's auto-generated names for graphql, so I import with "as". Is there
// a better way?
// NOTE: We intentionally do NOT import the auto-generated
// create/update/delete mutations for TaskPogoItem, TaskPogoCompletedTask,
// TaskPogoEmailSubscription, TaskPogoTask, or TaskPogoItemCounter from
// `../graphql/mutations.js`. Their selection sets request nested @hasOne
// blocks (e.g. `item { ... owner ... }`, `parentItem { ... owner ... }`,
// `itemCounter { ... owner ... }`, `Task { ... owner ... }`). With Amplify
// GraphQL Transformer v2 those nested resolvers run with the parent's
// `__operation == "Mutation"` marker and the per-field `owner` auth on the
// related types refuses to return the field, surfacing as
//   "Not Authorized to access owner on type TaskPogoItem"
// plus a cascade of
//   "Cannot return null for non-nullable type 'ID' within parent
//    'TaskPogoItem'"
// errors -- even though the underlying create/update/delete succeeds. We
// define local minimal mutations alongside each function below that select
// only the scalar fields callers actually read.
//
// See `src/utilities/__tests__/Database.{create,delete}Task.test.jsx` for
// regression coverage of the pattern.

import {
  getTaskPogoItemCounter,
  getTaskPogoTask,
  listTaskPogoTasks as APIListTaskPogoTasks,
  tasksByItemIdAndName,
  itemsByParentId,
  itemCounterByItemId,
  listTaskPogoEmailSubscriptions,
} from '../graphql/queries.js';

import { urlForItemStorage } from '../fleet-shared/Urls.mjs';
import { uniqueLogbookName, entriesToCreateInputs } from '../fleet-shared/LogbookIO.mjs';
import { updateTaskBucketName, updateTaskBucketNameInTaskList, counterFromId, createCounterMapFromItem } from '../fleet-shared/TaskBuckets.mjs';
import { trimmedStringOrNull, databaseDateTimeString, copyOnlyKnownFields } from '../fleet-shared/Common.mjs';
import { taskFields} from '../fleet-shared/TaskFields.mjs';
import { itemFields } from '../fleet-shared/ItemFields.mjs';
import { emailFields } from '../fleet-shared/Email.mjs';
import {
  groupTrips,
  tripGroupKey,
  isNamedGroupKey,
  findTripGroup,
  newTripGroupId,
  utcDayIndex,
} from '../fleet-shared/TripGroups.mjs';
import { uniqueTripName, legsToCreateInputs } from '../fleet-shared/TripIO.mjs';
import { placeWriteAvailable } from './placeWrite.mjs';
// The same shape as placeWriteAvailable above, and for the same reason: a deploy
// gate a DOCUMENT is built from, not just a button. See the share-row block below.
import { shareUploadAvailable } from './shareUpload.mjs';
import { shareTasksWriteAvailable } from './shareTasksWrite.mjs';

import { generateClient } from 'aws-amplify/api';
export const client = generateClient();


export const NewItemName = 'New Item';
export const NewTaskName = 'New Task';
export const NewCounterName = 'New Counter';
export const NewPlaceName = 'New Place';

// Local minimal create-task mutation. We intentionally do NOT request the
// nested @hasOne `item` and `itemCounter` blocks. With Amplify GraphQL
// Transformer v2, those nested resolvers run with the parent's
// `__operation == "Mutation"` marker and the per-field `owner` auth on the
// related types refuses to return the field, which surfaces as
// "Not Authorized to access owner on type TaskPogoItem" plus a cascade of
// "Cannot return null for non-nullable type 'ID' within parent
// 'TaskPogoItem'" errors. Callers only need scalar task fields here (a freshly
// created task has no itemCounterId yet, and the parent item is already in
// hand), so the minimal selection set both works around the bug and is more
// efficient over the wire.
export const APICreateTask = /* GraphQL */ `
  mutation CreateTaskPogoTask(
    $input: CreateTaskPogoTaskInput!
    $condition: ModelTaskPogoTaskConditionInput
  ) {
    createTaskPogoTask(input: $input, condition: $condition) {
      id
      name
      assignedEmail
      priority
      rank
      cost
      itemId
      description
      notes
      dueCounter
      dueDate
      counterInterval
      daysInterval
      monthsInterval
      itemCounterId
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

// Adds a new task to the Database for this item, throws on error. Returns the createTask object returned by the API.
export async function createTask(item, name = NewTaskName) {
    // Add to the database
    let newTask = {
        name: name,
        itemId: item.id,
        priority: 0,
        assignedEmail: '',
        rank: new Date().getDate(),
        dueDate: null
    };

    // Note: this can throw (e.g. network or security error)
    const created = await client.graphql({ query: APICreateTask, variables: { input: newTask }});
    var task = created.data.createTaskPogoTask;

    return task;
}

// Retrieve a task from the database, note this can throw for network issues, etc.
// Parameters:
//   id - The id of the task to retrieve.
// Returns task.
export async function retrieveTask(id, item) {
    let counterMap = createCounterMapFromItem(item);

    // Get the task
    var taskData = await client.graphql({ query: getTaskPogoTask, variables: { id: id } });
    const task = taskData.data.getTaskPogoTask;
    if (null == task) {
        throw new Error('Task not found');
    }

    // Put the bucket name in to the task, over the item's due-soon window.
    updateTaskBucketName(task, counterMap, item);
    return task;
}

// Retrieve a single item counter by id from the backend. Used to resolve a
// counter that isn't present in an already-loaded item's in-memory counter
// list -- e.g. one created in another tab/session after the item was loaded.
// Returns the counter object ({ id, name, counter, itemId, ... }), or null if
// no counter with that id exists. Can throw on network/security errors.
export async function retrieveCounter(id) {
    const counterData = await client.graphql({ query: getTaskPogoItemCounter, variables: { id: id } });
    return counterData.data.getTaskPogoItemCounter;
}


const APIUpdateTask = /* GraphQL */ `
  mutation UpdateTaskPogoTask(
    $input: UpdateTaskPogoTaskInput!
    $condition: ModelTaskPogoTaskConditionInput
  ) {
    updateTaskPogoTask(input: $input, condition: $condition) {
      id
      name
      assignedEmail
      priority
      rank
      cost
      itemId
      description
      notes
      dueCounter
      dueDate
      counterInterval
      daysInterval
      monthsInterval
      itemCounterId
    }
  }
`;

// Update the database, note this can throw for network issues,
// or if the task isn't quite right.
// Parameters:
//   task - The task to save to the database.
//   item - Item the task belongs to.
//   model - Optional [Tasks] the task is already in. If passed, the
//           matching entry is replaced in place with the saved task so
//           the caller's array reference stays in sync (used by
//           `useEditTaskPopup` to keep `item.tasks.items` current).
// Returns the saved task. The returned object is what AppSync echoed
// back, with the computed `taskBucket` field tagged on. Callers that
// passed `model` can rely on the array being mutated in place AND on the
// return value being the single saved task (not the array).
export async function updateTask(task, item, model) {
    task.name = trimmedStringOrNull(task.name);
    if ((null == task.name) || (task.name.length === 0)) {
      throw new Error("Name required");
    }

    // If the counter interval / due are filled in, need to have
    // an itemCounterId...  Go get it (or error if none).
    if ((task.dueCounter != null) && ((task.itemCounterId == null) || (0 === task.itemCounterId.length))) {
      throw new Error("Must fill in what counter based on, if have Due Counter or Interval set");
    }

    // The task has some extra fields (e.g. computed taskBucket), that the
    // Database API doesn't want. So create an object with only the fields
    // the database knows about. A field the user emptied comes through as an
    // explicit null, which is what actually clears it on the server -- except
    // for the optional GSI keys below, which have to be omitted instead:
    // `itemCounterId` keys the tasksByItemCounterId index and `dueCounter` is
    // its sort key, and DynamoDB rejects a NULL index key (see
    // `withoutNullIndexKeys`). Their stored values therefore survive a clear.
    let saveTask = withoutEmptyKeys(copyOnlyKnownFields(task, taskFields), ['itemCounterId', 'dueCounter']);
    saveTask.id = task.id;

    // Some fields are required for the database, but not for the user.
    if (!saveTask.dueDate) {
      saveTask.dueDate = null;
    }
    if (!saveTask.assignedEmail) {
      saveTask.assignedEmail = '';
    }
    if (!saveTask.priority) {
      saveTask.priority = -1;
    }

    var save = await client.graphql({ query: APIUpdateTask, variables: {input: saveTask }});
    var newTask = save.data.updateTaskPogoTask;

    // Add calculated fields to the new task. Note the new bucket name might not
    // be the same as the old due to changes made to task.
    let counterMap = createCounterMapFromItem(item);
    updateTaskBucketName(newTask, counterMap, item);

    // If the caller passed in a model, splice the saved task into it in
    // place so their array reference stays in sync. We still return the
    // saved task itself -- callers like `useEditTaskPopup.saveEditedTask`
    // need to read fields off the saved task (e.g. itemCounterId) right
    // after the call.
    if (model) {
      for (var i = 0; i < model.length; i++) {
        if (newTask.id === model[i].id) {
          model[i] = newTask;
          break;
        }
      }
    }
    return newTask;
}


// Local minimal delete-task mutation. Same rationale as `APICreateTask` /
// `APIUpdateTask` above: the auto-generated `deleteTaskPogoTask` selection
// set requests nested `item { ... owner ... }` and
// `itemCounter { ... owner ... }` blocks. With Amplify GraphQL Transformer
// v2 those nested @hasOne resolvers run with the parent's
// `__operation == "Mutation"` marker and the per-field `owner` auth on the
// related types refuses to return the field, surfacing as
// "Not Authorized to access owner on type TaskPogoItem" plus a cascade of
// "Cannot return null for non-nullable type 'ID' within parent
// 'TaskPogoItem'" errors -- even though the delete itself succeeds. The
// caller doesn't need anything beyond the id of the deleted row, so a
// scalar-only selection set both fixes the spurious error and trims the
// payload.
export const APIDeleteTask = /* GraphQL */ `
  mutation DeleteTaskPogoTask(
    $input: DeleteTaskPogoTaskInput!
    $condition: ModelTaskPogoTaskConditionInput
  ) {
    deleteTaskPogoTask(input: $input, condition: $condition) {
      id
      __typename
    }
  }
`;

// Update the database, note this can throw for network issues, 
// or if the task isn't  in the database.
// Parameters:
//   id - The id of the task to delete from the database.
//   model - Optional [Tasks] the task is already in that the task is removed from.
// Returns model with task removed.
export async function deleteTask(id, model) {
    // Delete on server
    await client.graphql({ query: APIDeleteTask, variables: { input: { id: id } }});

    // Caller didn't pass in a model? Nothing more to do...
    if (!model) { return null; }

    // Update model
    for (var i=0; i < model.length; i++) {
        if (id === model[i].id) {
            model.splice(i, 1); 
            break;
        }
    }
    return model;
}

// Lists all tasks for an item.
export async function listTasks(item) {
    let items;

    let counterMap = createCounterMapFromItem(item);

    let fetch = await client.graphql({query: APIListTaskPogoTasks, variables: { itemId: item.id }});
    items = fetch.data.listTaskPogoTasks.items;

    // Add calculated fields to the tasks.
    updateTaskBucketNameInTaskList(items, counterMap, item);

    return items;
}



const APICreateItemCounter = /* GraphQL */ `
  mutation CreateTaskPogoItemCounter(
    $input: CreateTaskPogoItemCounterInput!
    $condition: ModelTaskPogoItemCounterConditionInput
  ) {
    createTaskPogoItemCounter(input: $input, condition: $condition) {
      id
      name
      counter
      itemId
    }
  }
`;

export async function createItemCounter(newItem) {
    // Note: this can throw (e.g. network or security error)
    const createdCounter = await client.graphql({ query: APICreateItemCounter, variables: { input: newItem }});

    console.log(`Created counter: ${createdCounter.id}`);

    return createdCounter.data.createTaskPogoItemCounter;
}


const APIUpdateItemCounter = /* GraphQL */ `
  mutation UpdateTaskPogoItemCounter(
    $input: UpdateTaskPogoItemCounterInput!
    $condition: ModelTaskPogoItemCounterConditionInput
  ) {
    updateTaskPogoItemCounter(input: $input, condition: $condition) {
      id
      name
      counter
      itemId
    }
  }
`;
// Update the database, note this can throw for network issues, 
// or if the counter isn't quite right.
// Parameters:
//   counter - The counter to save to the database.
//   model - Optional [Counter] the counter is already in that is updated with the new counter.
// Returns model with updated counter.
export async function updateCounter(counter, model) {
    counter.name = trimmedStringOrNull(counter.name);
    if ((null == counter.name) || (counter.name.length === 0)) {
      throw new Error("Name required");
    }

    // Go do the update on the server -- can throw!
    var updated = await client.graphql({ query: APIUpdateItemCounter, variables: {input: counter }});
    var newCounter = updated.data.updateTaskPogoItemCounter;

    // Caller didn't pass in a model? Nothing more to do...
    if (!model) { return null; }

    // Update the task in the view's model.
    for (var i=0; i < model.length; i++) {
      if (newCounter.id === model[i].id) {
        model[i] = newCounter;
        break;
      }
    }
    return model;
}


export const APIDeleteItemCounter = /* GraphQL */ `
  mutation DeleteTaskPogoItemCounter(
    $input: DeleteTaskPogoItemCounterInput!
    $condition: ModelTaskPogoItemCounterConditionInput
  ) {
    deleteTaskPogoItemCounter(input: $input, condition: $condition) {
      id
    }
  }
`;
// Update the database, note this can throw for network issues, 
// or if the task isn't  in the database.
// Parameters:
//   id - The id of the item to delete from the database.
//   model - Optional [items] the item is already in that the item is removed from.
// Returns model with item removed.
export async function deleteCounter(id, model) {
    // First see if anyone depends on the Counter. In order to do so, need to query the Counter (we didn't
    // get enough data back in our initial query to know this).
    const counterItem = await client.graphql({ query: getTaskPogoItemCounter, variables: { id: id } });
    if ((counterItem.data.getTaskPogoItemCounter.tasks) && 
       (counterItem.data.getTaskPogoItemCounter.tasks.items) && 
       (counterItem.data.getTaskPogoItemCounter.tasks.items.length > 0)) 
    {
      var itemNames = "";
      for (var i=0; i < counterItem.data.getTaskPogoItemCounter.tasks.items.length; i++) {
        if (itemNames.length > 0) {
          itemNames = itemNames + ", ";
        }
        itemNames = itemNames + counterItem.data.getTaskPogoItemCounter.tasks.items[i].name;
      }

      throw new Error(`Cannot delete - '${itemNames}' based on this counter.`)
    }

    // Delete on server
    await client.graphql({ query: APIDeleteItemCounter, variables: { input: { id: id } }});

    // Caller didn't pass in a model? Nothing more to do...
    if (!model) { return null; }

    // Update model
    for (i=0; i < model.length; i++) {
        if (id === model[i].id) {
            model.splice(i, 1); 
            break;
        }
    }
    return model;
}

const deepGetItem = /* GraphQL */ `
  query GetTaskPogoItem($id: ID!) {
    getTaskPogoItem(id: $id) {
      id
      name
      description
      manufacturer
      serialNumber
      partNumber
      location
      notes
      dateInstalled
      counterInstalled
      soonDays
      soonCounter
      itemType
      tracking
      visibility
      parentId
      createdAt
      updatedAt
      owner
      parentItem {
        id
        name
      }
      childItems {
        items {
          id
          name
        }
        nextToken
      }
      tasks {
        items {
          id
          name
          itemId
          description
          notes
          dueCounter
          dueDate
          counterInterval
          daysInterval
          monthsInterval
          itemCounterId
        }
        nextToken
      }
      completedTasks(limit: 5, sortDirection: DESC) {
        items {
          id
          name
          itemId
          logbookId
          itemCounterId
          taskId
          description
          taskNotes
          dueCounter
          dueDate
          counterInterval
          daysInterval
          monthsInterval
          notes
          counter
          date
          fields
        }
      }
      itemCounter {
        items {
          id
          name
          counter
          itemId
          createdAt
          updatedAt
        }
        nextToken
      }
      emails {
        items {
          id
          emailType
          name
          email
          sendHourGMT
          itemId
          sendEveryDays
          lastSentDate
          nextSendDate
          notifyOnTaskStateChange
          transitionNextSendDate
          digestMode
        }
      }
    }
  }
`;
export async function retrieveItem(id) {
    var itemData = await client.graphql({ query: deepGetItem, variables: { id: id } });
    const item = itemData.data.getTaskPogoItem;

    // deepGetItem returns each nested connection capped at a single AppSync page
    // (default 100) and only surfaces a nextToken. When a connection is
    // truncated, fetch the full set via its by-itemId index query so an item
    // with >100 tasks / children / counters doesn't silently lose the tail. We
    // only round-trip when there's actually a second page. (completedTasks is
    // intentionally limited to 5 and is fetched separately by callers via
    // listRecentCompletedTasks; emails don't select a nextToken.)
    // collectAllPages walks every page from scratch, so the result is the
    // complete set with no more to fetch -- null out the connection's nextToken
    // too, otherwise the returned object is inconsistent (full .items but a
    // stale token that says "more pages exist").
    if (item) {
      if (item.tasks && item.tasks.nextToken) {
        // Query the itemId GSI (tasksByItemIdAndName) rather than scanning the
        // whole Tasks table with a filter -- the partition key is itemId, so
        // this reads only this item's tasks. (The sort-key `name` condition is
        // optional; omitting it returns every task for the item.)
        item.tasks.items = await collectAllPages(
          tasksByItemIdAndName,
          { itemId: id },
          'tasksByItemIdAndName'
        );
        item.tasks.nextToken = null;
      }
      if (item.childItems && item.childItems.nextToken) {
        item.childItems.items = await collectAllPages(
          itemsByParentId,
          { parentId: id },
          'itemsByParentId'
        );
        item.childItems.nextToken = null;
      }
      if (item.itemCounter && item.itemCounter.nextToken) {
        item.itemCounter.items = await collectAllPages(
          itemCounterByItemId,
          { itemId: id },
          'itemCounterByItemId'
        );
        item.itemCounter.nextToken = null;
      }
    }

    return item;
}

// Every item the signed-in user owns -- id, name and the list-level flags
// (plus parentId, so a caller can keep to top-level items), every page, no
// parent filter. The Items page deliberately does NOT use this: it paginates
// top-level items through infinite scroll. This is for callers that need the
// whole fleet at once and decide for themselves which items to show, like the
// cross-item Trips page (where sub-equipment with its own log qualifies too).
const APIListItemNames = /* GraphQL */ `
  query ListTaskPogoItems(
    $filter: ModelTaskPogoItemFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listTaskPogoItems(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        itemType
        tracking
        visibility
        parentId
      }
      nextToken
    }
  }
`;

export async function listAllItems() {
    return collectAllPages(APIListItemNames, {}, 'listTaskPogoItems');
}


// Local minimal item mutations. See the note at the top of this file -- the
// auto-generated TaskPogoItem mutations request a nested `parentItem
// { ... owner ... }` block that triggers a transformer-v2 owner-auth bug on
// the response. Callers only need scalar fields, so we select all of them
// (everything `itemFields` covers, plus the relationship key `parentId` and
// the standard scalars) and skip the nested @hasOne / @hasMany blocks.
export const APIUpdateItem = /* GraphQL */ `
  mutation UpdateTaskPogoItem(
    $input: UpdateTaskPogoItemInput!
    $condition: ModelTaskPogoItemConditionInput
  ) {
    updateTaskPogoItem(input: $input, condition: $condition) {
      id
      name
      description
      manufacturer
      serialNumber
      partNumber
      location
      notes
      dateInstalled
      counterInstalled
      soonDays
      soonCounter
      itemType
      tracking
      visibility
      parentId
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APICreateItem = /* GraphQL */ `
  mutation CreateTaskPogoItem(
    $input: CreateTaskPogoItemInput!
    $condition: ModelTaskPogoItemConditionInput
  ) {
    createTaskPogoItem(input: $input, condition: $condition) {
      id
      name
      description
      manufacturer
      serialNumber
      partNumber
      location
      notes
      dateInstalled
      counterInstalled
      soonDays
      soonCounter
      itemType
      tracking
      visibility
      parentId
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APIDeleteItem = /* GraphQL */ `
  mutation DeleteTaskPogoItem(
    $input: DeleteTaskPogoItemInput!
    $condition: ModelTaskPogoItemConditionInput
  ) {
    deleteTaskPogoItem(input: $input, condition: $condition) {
      id
      __typename
    }
  }
`;

// Saves an item edited in the UI. The UI item only includes fields the user
// can edit. So, doesn't include things like the item.id. That is copied in here.
// Returns the item returned by the database. Throws on errors.
export async function saveEditedItem(newItem, oldItem) {
    // `name` is required by the schema (and is the sort key of the
    // itemsByParentId index). An emptied name used to be quietly dropped from
    // the mutation, leaving the old one in place; now that a cleared field is
    // sent as an explicit null, say what went wrong instead of letting AppSync
    // reject it. Only checked when the caller set the field at all, so a
    // partial update that doesn't touch the name still works.
    if (Object.prototype.hasOwnProperty.call(newItem, 'name')) {
      newItem.name = trimmedStringOrNull(newItem.name);
      if (!newItem.name) {
        throw new Error("Name required");
      }
    }

    let save = copyOnlyKnownFields(newItem, itemFields);
    save.id = oldItem.id;
    const savedItem = await updateItem(save);
    return savedItem;
}

export async function updateItem(item) {
    // Go do the update -- can throw!
    var updateItem = await client.graphql({ query: APIUpdateItem, variables: {input: item }});
    return updateItem.data.updateTaskPogoItem;
}

// Walk every page of a paginated AppSync list/connection query, following
// `nextToken` until the backend stops returning one, and return the flattened
// `items` across all pages. The delete path relies on this so an item with more
// than one page (default 100) of children / tasks / counters / logbook entries
// can't orphan the tail of those records in DynamoDB. `connectionKey` is the
// top-level field name on `data` (e.g. 'itemsByParentId').
async function collectAllPages(query, variables, connectionKey) {
    let items = [];
    let nextToken = null;
    do {
        const { data } = await client.graphql({ query, variables: { ...variables, nextToken } });
        const connection = data[connectionKey];
        items = items.concat(connection.items);
        nextToken = connection.nextToken;
    } while (nextToken);
    return items;
}

// Delete the given item and all of its children, along with every related
// record (tasks, counters, email subscriptions, trips and the weather
// subscriptions and share links pointing at them, logbook entries) and S3 file.
//
// The passed `item` only needs to carry a valid `id`: all related collections
// are fetched fresh here -- with full pagination -- rather than read off
// whatever (possibly first-page-only) object the caller happened to load. That
// keeps the cascade complete for items with many children/tasks/logbook
// entries, and robust to being handed a partially-populated item.
export async function deleteItem(item) {
    // Delete any files associated with this item. This deletes files for any children,
    // too -- since storageList returns all files in a bucket and S3 has no real
    // concept of sub-directories...
    let files = await storageList(urlForItemStorage(item.id));
    for (var i=0; i < files.length; i++) {
        await storageDelete(files[i].key);
    }

    // Recursively delete all child items (equipment / parts). Fetch the full
    // child list by parentId so items with many children don't orphan the tail;
    // each child recurses through this same function.
    const children = await collectAllPages(itemsByParentId, { parentId: item.id }, 'itemsByParentId');
    for (i=0; i < children.length; i++) {
        await deleteItem(children[i]);
    }

    // Delete all our tasks.
    await deleteSubTasksOfItemId(item.id);

    // Delete Counters, if any. deleteCounter throws if a task still depends on
    // the counter; swallow that so one stuck counter doesn't abort the rest of
    // the cascade (matches prior behavior).
    const counters = await collectAllPages(itemCounterByItemId, { itemId: item.id }, 'itemCounterByItemId');
    for (i=0; i < counters.length; i++) {
        try {
            await deleteCounter(counters[i].id);
        } catch {
            // Do nothing if something still depends on the counter.
        }
    }

    // Delete email subscriptions.
    const emails = await collectAllPages(
        listTaskPogoEmailSubscriptions,
        { filter: { itemId: { eq: item.id } } },
        'listTaskPogoEmailSubscriptions'
    );
    for (i=0; i < emails.length; i++) {
        await deleteEmailSubscription(emails[i].id);
    }

    // Delete trip weather subscriptions. BEFORE the trips, so a subscription
    // never outlives the trip it points at even if the cascade is interrupted
    // part way. (TaskPogoTripWeatherMail would clean up a survivor on its next
    // run anyway; this just means there is nothing to clean up.)
    try {
        const weatherSubs = await listTripWeatherSubscriptionsForItem(item.id);
        for (i = 0; i < weatherSubs.length; i++) {
            await deleteTripWeatherSubscription(weatherSubs[i].id);
        }
    } catch (err) {
        // A branch whose schema predates this model answers with an error here.
        // Losing the item over it would be far worse than an orphan row that
        // the mail Lambda deletes the next time it looks.
        console.error('Could not remove the item\'s trip weather subscriptions', err);
    }

    // Revoke the item's public share links, BEFORE the trips for the same
    // reason -- and here it is the only cleanup there is. Unlike a weather
    // subscription, a share row has no Lambda that tidies up after it: the
    // shared page answers "gone" for a group with no legs, but the row itself
    // survives, and a legacy 'n:' key is nothing but a name -- so a trip
    // created later, on another item's page or this one's replacement, spelled
    // the same way, would inherit the link. Same swallow-and-log as above,
    // for the same reason. (TripGroup's Delete and Trip's last-leg delete run
    // the per-trip version of this; see shareTrip.removeTripShareForGroup.)
    try {
        const shares = await listTripSharesForItem(item.id);
        for (i = 0; i < shares.length; i++) {
            await deleteTripShare(shares[i].id);
        }
    } catch (err) {
        console.error('Could not revoke the item\'s trip share links', err);
    }

    // And its LOGBOOK trip links, same terms and same reason. Its own try/catch
    // rather than sharing the one above: a branch whose schema predates
    // TaskPogoLogbookTripShare answers with an error here, and that must not
    // take out the trip-share revoke that just succeeded.
    //
    // NOTE the asymmetry with the trip side, which ALSO revokes per-trip from
    // TripGroup's Delete and Trip's last-leg delete. A logbook has no "delete
    // this trip" gesture -- entries are deleted one at a time -- so the
    // equivalent check would be a whole-logbook read on EVERY entry delete.
    // This cascade and deleteLogbook's are the two cheap places, and what they
    // leave uncovered is confined to the legacy 'n:' key space: a 'g:' key is a
    // fresh uuid that cannot be re-minted, so the Lambda's 410 is the whole
    // story there.
    try {
        const logbookShares = await listLogbookTripSharesForItem(item.id);
        for (i = 0; i < logbookShares.length; i++) {
            await deleteLogbookTripShare(logbookShares[i].id);
        }
    } catch (err) {
        console.error('Could not revoke the item\'s logbook trip share links', err);
    }

    // And its "tasks assigned to X" links, same terms and same reason. Here the
    // orphan would be worse than untidy: the share row is a live pointer, and
    // its Lambda answers 410 for a missing ITEM -- so a row left behind is a
    // public URL that goes on resolving against an item id nothing owns.
    try {
        const assignedShares = await listAssignedTasksSharesForItem(item.id);
        for (i = 0; i < assignedShares.length; i++) {
            await deleteAssignedTasksShare(assignedShares[i].id);
        }
    } catch (err) {
        console.error('Could not revoke the item\'s assigned-tasks share links', err);
    }

    // Delete trips.
    const trips = await listTripsForItem(item.id);
    for (i = 0; i < trips.length; i++) {
        await deleteTrip(trips[i].id);
    }

    // Delete completed tasks entries (removes every entry for the item,
    // including any tied to a logbook).
    await deleteCompletedTasksOfItemId(item.id);

    // Delete the item's logbooks themselves. Their entries are already gone
    // above, so deleting the logbook rows won't re-scan for entries.
    const logbooks = await collectAllPages(APILogbooksByItemId, { itemId: item.id }, 'logbooksByItemId');
    for (i = 0; i < logbooks.length; i++) {
        await client.graphql({ query: APIDeleteLogbook, variables: { input: { id: logbooks[i].id } } });
    }

    // Finally, delete this item.
    await client.graphql({ query: APIDeleteItem, variables: { input: { id: item.id } }});
}

// Delete all tasks related to the given item ID (every page).
export async function deleteSubTasksOfItemId(id) {
  const tasks = await collectAllPages(
    APIListTaskPogoTasks,
    { filter: { itemId: { eq: id } } },
    'listTaskPogoTasks'
  );

  for (var i=0; i < tasks.length; i++) {
    await deleteTask(tasks[i].id);
  }
}

// Delete all logbook entries related to the given item ID.
export async function deleteCompletedTasksOfItemId(id) {
  const logbookData = await listCompletedTasks(id);

  for (var i=0; i < logbookData.length; i++) {
    await deleteCompletedTask(logbookData[i].id);
  }
}

export async function createItem(newItem) {
    // Note: this can throw (e.g. network or security error)
    const createdItem = await client.graphql({ query: APICreateItem, variables: { input: newItem }});

    console.log(`Created item: ${createdItem.id}`);

    return createdItem.data.createTaskPogoItem;
}


// Local minimal completed-task mutations. See the note at the top of this
// file -- the auto-generated TaskPogoCompletedTask mutations request nested
// `item { ... owner ... }`, `itemCounter { ... owner ... }`, and
// `Task { ... owner ... }` blocks that trigger a transformer-v2 owner-auth
// bug on the response. Callers only read scalar fields off the result, so
// we select all of them and skip the nested @hasOne blocks.
export const APICreateCompletedTask = /* GraphQL */ `
  mutation CreateTaskPogoCompletedTask(
    $input: CreateTaskPogoCompletedTaskInput!
    $condition: ModelTaskPogoCompletedTaskConditionInput
  ) {
    createTaskPogoCompletedTask(input: $input, condition: $condition) {
      id
      name
      cost
      fixByEmail
      itemId
      logbookId
      date
      itemCounterId
      taskId
      description
      taskNotes
      dueCounter
      dueDate
      counterInterval
      daysInterval
      monthsInterval
      notes
      counter
      fields
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APIUpdateCompletedTask = /* GraphQL */ `
  mutation UpdateTaskPogoCompletedTask(
    $input: UpdateTaskPogoCompletedTaskInput!
    $condition: ModelTaskPogoCompletedTaskConditionInput
  ) {
    updateTaskPogoCompletedTask(input: $input, condition: $condition) {
      id
      name
      cost
      fixByEmail
      itemId
      logbookId
      date
      itemCounterId
      taskId
      description
      taskNotes
      dueCounter
      dueDate
      counterInterval
      daysInterval
      monthsInterval
      notes
      counter
      fields
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APIDeleteCompletedTask = /* GraphQL */ `
  mutation DeleteTaskPogoCompletedTask(
    $input: DeleteTaskPogoCompletedTaskInput!
    $condition: ModelTaskPogoCompletedTaskConditionInput
  ) {
    deleteTaskPogoCompletedTask(input: $input, condition: $condition) {
      id
      __typename
    }
  }
`;

// DynamoDB rejects a write that sets a GSI key attribute to an explicit NULL
// ("One or more parameter values were invalid: Type mismatch for Index Key
// <key> Expected: S Actual: NULL"). A CompletedTask's `itemCounterId`
// (gsi-TaskPogoItemCounter.completedTasks) and `taskId`
// (gsi-TaskPogoTask.completedTasks / completedTasksByTaskId) are both optional
// GSI keys, so when they're empty we must OMIT them from the mutation input
// rather than send `null` -- an absent attribute is simply not indexed, which
// is exactly what we want for an entry that isn't tied to a counter/task.
// (Gen 1's resolvers tolerated the null; Gen 2's do not.) We clone first so we
// never mutate the caller's object, which the UI may still read from.
const OPTIONAL_INDEX_KEYS = ['itemCounterId', 'taskId', 'logbookId'];

// Drop `keys` from a mutation input when they're empty. Every model with
// optional GSI keys needs this, for the reason above -- `copyOnlyKnownFields`
// now carries a cleared field through as an explicit null so it really gets
// removed, which is right for ordinary attributes and fatal for index keys.
// Clones, so the caller's object is never touched.
function withoutEmptyKeys(input, keys) {
  const cleaned = { ...input };
  for (const key of keys) {
    if (cleaned[key] === null || cleaned[key] === undefined || cleaned[key] === '') {
      delete cleaned[key];
    }
  }
  return cleaned;
}

function withoutNullIndexKeys(input) {
  const cleaned = withoutEmptyKeys(input, OPTIONAL_INDEX_KEYS);
  // The `fields` JSON blob (type-specific logbook columns) is an AWSJSON
  // scalar, which AppSync expects as a JSON *string*. Serialize plain objects
  // before they hit the mutation (strings pass through untouched so callers can
  // pre-serialize if they want). An empty/absent blob is omitted entirely.
  if (cleaned.fields != null && typeof cleaned.fields === 'object') {
    if (Object.keys(cleaned.fields).length === 0) {
      delete cleaned.fields;
    } else {
      cleaned.fields = JSON.stringify(cleaned.fields);
    }
  }
  return cleaned;
}

export async function createCompletedTask(newCompletedTask) {
    const created = await client.graphql({ query: APICreateCompletedTask, variables: { input: withoutNullIndexKeys(newCompletedTask) }});

    console.log(`Created completed task: ${created.data.createTaskPogoCompletedTask.id}`);

    return created.data.createTaskPogoCompletedTask;
}

// Creates a completed task, showing UI while adding/errors. Calls onAdded(newTask) on success.
// `logbook` is the logbook the entry belongs to ({ id, itemId, ... }); the new
// entry is tied to it via `logbookId` so it shows up in that logbook's list.
export async function createCompletedTaskWithUI(logbook, showText, showError, hideStatus, onAdded) {
  try {
    showText('Adding...');

    let newTask = {
      name: 'New Entry',
      date: databaseDateTimeString(new Date()),
      itemId: logbook.itemId,
      logbookId: logbook.id
    };
    let savedTask = await createCompletedTask(newTask);

    console.log(`Created completed task from ui: ${savedTask.id}`);

    onAdded(savedTask);
    hideStatus();
  }
  catch (err) {
    showError(err);
  }
}

// Local single-entry read. The auto-generated `getTaskPogoCompletedTask`
// predates typed logbooks and selects neither `logbookId` nor the `fields`
// JSON blob, so an entry loaded through it (i.e. any deep link or row click
// that doesn't hand the entry over in router state) rendered every
// type-specific column -- Trip Name, From, To, counters, Hours -- as blank,
// and saving that form would have written the blob back empty. It also pulls
// nested `item`/`itemCounter` blocks nobody reads; only `Task` is used (by
// useSharedItemPageState for the logbook-entry-from-task page).
const APIGetCompletedTask = /* GraphQL */ `
  query GetTaskPogoCompletedTask($id: ID!) {
    getTaskPogoCompletedTask(id: $id) {
      id
      name
      cost
      fixByEmail
      itemId
      logbookId
      date
      itemCounterId
      taskId
      Task {
        id
        name
        assignedEmail
        priority
        rank
        cost
        itemId
        description
        notes
        dueCounter
        dueDate
        counterInterval
        daysInterval
        monthsInterval
        itemCounterId
        owner
        createdAt
        updatedAt
        __typename
      }
      description
      taskNotes
      dueCounter
      dueDate
      counterInterval
      daysInterval
      monthsInterval
      notes
      counter
      fields
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export async function retrieveCompletedTask(id) {
    // Get the task
    var taskData = await client.graphql({ query: APIGetCompletedTask, variables: { id: id } });
    const task = taskData.data.getTaskPogoCompletedTask;
    return task;
}

export async function updateCompletedTask(task) {
    // Go do the update -- can throw! Strip empty optional GSI keys (see
    // withoutNullIndexKeys) so we don't try to write a NULL index key.
    var updateItem = await client.graphql({ query: APIUpdateCompletedTask, variables: {input: withoutNullIndexKeys(task) }});
    return updateItem.data.updateTaskPogoCompletedTask;
}


// Scalar-only existence probe. Same rationale as the minimal mutations above:
// we only need to know whether the row is still readable.
const APIGetCompletedTaskId = /* GraphQL */ `
  query GetTaskPogoCompletedTask($id: ID!) {
    getTaskPogoCompletedTask(id: $id) {
      id
      __typename
    }
  }
`;

// Whether a logbook entry is still readable. If the probe itself fails we say
// "yes" so the caller reports its original error rather than this one.
async function completedTaskExists(id) {
    try {
        const result = await client.graphql({ query: APIGetCompletedTaskId, variables: { id: id } });
        return result.data.getTaskPogoCompletedTask != null;
    } catch {
        return true;
    }
}

// Deletes a logbook entry. AppSync authorizes the delete by reading the row
// first and comparing its `owner` to the caller, so deleting a row that is
// ALREADY GONE fails that check with
//   "Not Authorized to access deleteTaskPogoCompletedTask on type Mutation"
// rather than a not-found error. The cascades below can legitimately reach an
// already-deleted entry (a re-run or double-submitted delete, or a stale read
// of the eventually-consistent completedTasksBy* index), and that must not
// abort the rest of the cascade -- so on failure we re-check and treat a
// missing row as success. A row that is still there means the failure was real.
export async function deleteCompletedTask(id) {
    try {
        const deleted = await client.graphql({ query: APIDeleteCompletedTask, variables: { input: { id: id } }});
        return deleted.data.deleteTaskPogoCompletedTask;
    } catch (err) {
        if (await completedTaskExists(id)) {
            throw err;
        }
        return null;
    }
}


// Same story as APIGetCompletedTask above: the auto-generated
// completedTasksByItemId omits `logbookId` and the `fields` blob, and the item
// page hands the rows it loads straight to the entry detail page through
// router state -- so without them that page renders blank.
const APICompletedTasksByItemId = /* GraphQL */ `
  query CompletedTasksByItemId(
    $itemId: ID!
    $date: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelTaskPogoCompletedTaskFilterInput
    $limit: Int
    $nextToken: String
  ) {
    completedTasksByItemId(
      itemId: $itemId
      date: $date
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        cost
        fixByEmail
        itemId
        logbookId
        date
        itemCounterId
        taskId
        description
        taskNotes
        dueCounter
        dueDate
        counterInterval
        daysInterval
        monthsInterval
        notes
        counter
        fields
        owner
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

export async function listCompletedTasks(itemId) {
  // Paginate so the logbook delete (deleteCompletedTasksOfItemId) sees every
  // entry -- items with a long history can run well past a single page.
  return collectAllPages(
    APICompletedTasksByItemId,
    { itemId: itemId, sortDirection: 'DESC' },
    'completedTasksByItemId'
  );
}


// Returns the most recent `limit` completed tasks for an item, newest first.
// Uses the completedTasksByItemId index (sortKeys ['date']) so the order is by
// date -- the hasMany relationship on Item does NOT sort by date. Fetch one
// extra (limit) so callers can tell whether more entries exist beyond what they
// show.
export async function listRecentCompletedTasks(itemId, limit = 6) {
  const result = await client.graphql({
    query: APICompletedTasksByItemId,
    variables: { itemId: itemId, sortDirection: 'DESC', limit: limit },
  });
  return result.data.completedTasksByItemId.items;
}


// Copy a Task into a new logbook entry (CompletedTask) in the given logbook.
// `logbookId` ties the entry to its logbook. Only Maintenance logbooks take
// part in this flow (they carry the was-due / interval / recurrence data).
export async function copyTaskToCompletedTasks(task, item, logbookId) {
  // Pull counter value from the optional counter associated with task. We
  // intentionally leave `counter` as null (vs. 0) when the task isn't tied
  // to a counter -- 0 is a valid reading, but a missing counter should stay
  // missing so the logbook list renders an empty cell rather than "0".
  // `counterFromId` returns -1 when the counter can't be resolved.
  let counter = null;
  if (task.itemCounterId) {
    const resolved = counterFromId(item, task.itemCounterId);
    if (resolved !== -1) {
      counter = resolved;
    }
  }

  let completedTask = {
    date: databaseDateTimeString(new Date()),
    counter: counter, 
    name: task.name,
    description: task.description,
    taskNotes: task.notes,
    cost: task.cost,
    fixByEmail: task.assignedEmail,
    dueCounter: task.dueCounter,
    itemCounterId: task.itemCounterId,
    dueDate: task.dueDate,
    counterInterval: task.counterInterval,
    daysInterval: task.daysInterval,
    monthsInterval: task.monthsInterval,
    itemId: task.itemId,
    logbookId: logbookId,
    taskId: task.id
  };

  // Delegate to createCompletedTask so the returned object is the persisted
  // record (carrying its server-assigned `id`). Callers navigate to the new
  // entry via `newTask.id`, so returning `completedTask` directly here -- which
  // has no `id` yet -- would break the logbook-entry navigation.
  return await createCompletedTask(completedTask);
}


// ***** Logbooks *****
//
// A Logbook is a typed collection of CompletedTask entries. An Item may have
// zero or more. See src/fleet-shared/LogbookTypes.mjs for the type registry.
// As with the other models we hand-write minimal scalar-only operations to
// avoid the transformer-v2 nested-owner-auth bug (see the note at the top of
// this file).

const APICreateLogbook = /* GraphQL */ `
  mutation CreateTaskPogoLogbook(
    $input: CreateTaskPogoLogbookInput!
    $condition: ModelTaskPogoLogbookConditionInput
  ) {
    createTaskPogoLogbook(input: $input, condition: $condition) {
      id
      name
      logbookType
      itemId
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

const APIUpdateLogbook = /* GraphQL */ `
  mutation UpdateTaskPogoLogbook(
    $input: UpdateTaskPogoLogbookInput!
    $condition: ModelTaskPogoLogbookConditionInput
  ) {
    updateTaskPogoLogbook(input: $input, condition: $condition) {
      id
      name
      logbookType
      itemId
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

const APIDeleteLogbook = /* GraphQL */ `
  mutation DeleteTaskPogoLogbook(
    $input: DeleteTaskPogoLogbookInput!
    $condition: ModelTaskPogoLogbookConditionInput
  ) {
    deleteTaskPogoLogbook(input: $input, condition: $condition) {
      id
      __typename
    }
  }
`;

const APILogbooksByItemId = /* GraphQL */ `
  query LogbooksByItemId(
    $itemId: ID!
    $name: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelTaskPogoLogbookFilterInput
    $limit: Int
    $nextToken: String
  ) {
    logbooksByItemId(
      itemId: $itemId
      name: $name
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        logbookType
        itemId
        owner
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

const APIGetLogbook = /* GraphQL */ `
  query GetTaskPogoLogbook($id: ID!) {
    getTaskPogoLogbook(id: $id) {
      id
      name
      logbookType
      itemId
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

const APICompletedTasksByLogbookId = /* GraphQL */ `
  query CompletedTasksByLogbookId(
    $logbookId: ID!
    $date: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelTaskPogoCompletedTaskFilterInput
    $limit: Int
    $nextToken: String
  ) {
    completedTasksByLogbookId(
      logbookId: $logbookId
      date: $date
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        cost
        fixByEmail
        itemId
        logbookId
        date
        itemCounterId
        taskId
        description
        taskNotes
        dueCounter
        dueDate
        counterInterval
        daysInterval
        monthsInterval
        notes
        counter
        fields
        owner
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

// Lists all logbooks for an item, sorted by name.
export async function listLogbooks(itemId) {
  return collectAllPages(
    APILogbooksByItemId,
    { itemId: itemId },
    'logbooksByItemId'
  );
}

export async function retrieveLogbook(id) {
  const result = await client.graphql({ query: APIGetLogbook, variables: { id: id } });
  return result.data.getTaskPogoLogbook;
}

// Creates a logbook for an item. `logbook` = { itemId, name, logbookType }.
export async function createLogbook(logbook) {
  const created = await client.graphql({ query: APICreateLogbook, variables: { input: logbook } });
  return created.data.createTaskPogoLogbook;
}

// Renames / updates a logbook. `logbook` must carry its id.
export async function updateLogbook(logbook) {
  const updated = await client.graphql({ query: APIUpdateLogbook, variables: { input: logbook } });
  return updated.data.updateTaskPogoLogbook;
}

// Deletes a logbook and cascade-deletes all of its entries. Entries go one at a
// time, so `onProgress(done, total)` lets the caller show a counter -- a logbook
// with a few hundred entries takes a while.
export async function deleteLogbook(id, { onProgress } = {}) {
  // Revoke the logbook's public trip links FIRST, so a link never outlives the
  // entries it points at even if the cascade is interrupted part way. The
  // shared page already answers "gone" for a trip with no entries, so this is
  // not what stops a dead link resolving -- what it stops is a link being
  // INHERITED: a legacy 'n:' key is nothing but a name, so a trip created later
  // and spelled the same way would pick up the old row.
  //
  // Best-effort and swallowed, like the item cascade's: losing the logbook over
  // a failed cleanup would be far worse than an orphan row. A branch whose
  // schema predates the model answers with an error here too.
  try {
    const shares = await listLogbookTripSharesForLogbook(id);
    for (const share of shares) {
      await deleteLogbookTripShare(share.id);
    }
  } catch (err) {
    console.error("Could not revoke the logbook's trip share links", err);
  }

  await deleteCompletedTasksOfLogbookId(id, { onProgress });
  const deleted = await client.graphql({ query: APIDeleteLogbook, variables: { input: { id: id } } });
  return deleted.data.deleteTaskPogoLogbook;
}

// Imports a parsed logbook export (see src/fleet-shared/LogbookIO.mjs) as a
// BRAND-NEW logbook on the given item, so existing data is never touched. The
// logbook name is deduped against the item's current logbooks. Entries are
// created sequentially (honest progress + gentle on AppSync). On any failure
// the just-created logbook is cascade-deleted so a partial import leaves no
// user-visible data behind, and the ORIGINAL error is re-thrown for the
// caller's UI even if that cleanup itself fails.
// `parsed` = { logbook: { name, logbookType }, entries: [...], places: [...] }.
//
// `placeIndex` is this environment's gazetteer, AFTER the caller has ensured
// the file's places exist (places.mjs `ensurePlaces` — which lives there
// because it has to invalidate that module's caches, and importing it here
// would invert the layering: places.mjs already imports this file). Given one,
// every imported route is bound against it, so the stored blob names rows that
// exist here rather than the ids of the environment that wrote the file.
// Without one the routes land unbound and rebind on their first read.
export async function importLogbook(itemId, parsed, { onProgress, placeIndex } = {}) {
  const existing = await listLogbooks(itemId);
  const name = uniqueLogbookName(
    parsed.logbook.name,
    existing.map((lb) => lb.name)
  );
  const logbookType = parsed.logbook.logbookType;

  const created = await createLogbook({ itemId, name, logbookType });
  try {
    const inputs = entriesToCreateInputs(parsed.entries, logbookType, {
      itemId,
      logbookId: created.id,
      placeIndex,
    });
    for (let i = 0; i < inputs.length; i++) {
      await createCompletedTask(inputs[i]);
      onProgress?.(i + 1, inputs.length, 'entries');
    }
  } catch (err) {
    // The logbook is brand new; cascading it away removes only what we added.
    // If that rollback fails too we log it and carry on: the caller needs to
    // see WHY the import failed, not the follow-on cleanup error.
    //
    // Deliberately does NOT delete any place row the caller created first.
    // Those are global, and another user may have bound a waypoint to one in
    // the meantime -- deleting it would be far worse than leaving an orphan,
    // which a retry reuses anyway.
    try {
      await deleteLogbook(created.id);
    } catch (rollbackErr) {
      console.error(`Failed to roll back imported logbook ${created.id}:`, rollbackErr);
    }
    throw err;
  }
  return created;
}

// Lists all completed-task entries for a logbook (every page), newest first.
export async function listCompletedTasksForLogbook(logbookId) {
  return collectAllPages(
    APICompletedTasksByLogbookId,
    { logbookId: logbookId, sortDirection: 'DESC' },
    'completedTasksByLogbookId'
  );
}

// The entries of one logbook that fall on the same calendar day as `date`,
// for "is this day already logged?".
//
// Deliberately not listCompletedTasksForLogbook + filter: that pages the WHOLE
// logbook, and a Captain's Log accumulates an entry per day forever. The
// completedTasksByLogbookId GSI is sorted by date and the hand-written query
// already declares `$date: ModelStringKeyConditionInput`, so the window is a
// key condition rather than a scan.
//
// The window is widened by a day on each side and the exact day is then decided
// in JS on utcDayIndex. Entry dates are not all spelled the same way --
// Common.databaseDateTimeString writes a LOCAL-OFFSET timestamp, which does not
// sort lexicographically against a `Z` one -- so a tight between() would
// silently miss a row written in the other spelling. A duplicate check that
// answers "no duplicate" wrongly is worse than no check at all; +/- 1 day
// covers any real UTC offset (max +/- 14h) and costs a handful of rows.
export async function listLogbookEntriesOnDay(logbookId, date) {
  const day = utcDayIndex(date);
  if (!logbookId || day == null) return [];

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const midnight = day * MS_PER_DAY;
  const from = new Date(midnight - MS_PER_DAY).toISOString();
  const to = new Date(midnight + 2 * MS_PER_DAY).toISOString();

  const near = await collectAllPages(
    APICompletedTasksByLogbookId,
    { logbookId: logbookId, date: { between: [from, to] } },
    'completedTasksByLogbookId'
  );
  return near.filter((entry) => utcDayIndex(entry?.date) === day);
}

// How wide a page the previous-entry read below asks for. One page, not a
// page-through: the answer is the FIRST row below the window, and a logbook
// would have to hold 50 entries on a single day to push it off this page.
const PREVIOUS_ENTRY_PAGE = 50;

// The entry immediately before `entry` in its logbook, by date. Null when there
// isn't one. Feeds the Start Counter carry-forward suggestion.
//
// Same shape as listLogbookEntriesOnDay above, and for the same reason: the GSI
// is sorted by date, so this is a key condition rather than a page-through of a
// logbook that accumulates an entry per day forever.
//
// TWO STEPS, and the second is not optional. The key condition is deliberately
// LOOSE (`lt` a day AFTER this entry, so it can't cut off a row that sorts
// oddly) and the real ordering is then done in JS on parsed instants. Entry
// dates are not all spelled the same way -- Common.databaseDateTimeString
// writes a LOCAL-OFFSET timestamp, which does not sort lexicographically
// against a `Z` one -- so a tight `lt` would silently skip the very row this is
// looking for, and a wrong Start Counter is worse than no suggestion.
//
// Entries at the SAME instant have no defined order and neither is "prior" to
// the other. Dates here are date-only (dateFieldChanged normalizes to local
// midnight), so that is exactly the two-entries-on-one-day case: the suggestion
// falls through to the previous DAY rather than picking one of a pair
// arbitrarily.
export async function previousLogbookEntry(logbookId, entry) {
    const at = Date.parse(entry?.date ?? '');
    if (!logbookId || !entry?.id || Number.isNaN(at)) return null;

    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const before = new Date(at + MS_PER_DAY).toISOString();

    const result = await client.graphql({
      query: APICompletedTasksByLogbookId,
      variables: {
        logbookId: logbookId,
        date: { lt: before },
        sortDirection: 'DESC',
        limit: PREVIOUS_ENTRY_PAGE,
      },
    });

    const earlier = result.data.completedTasksByLogbookId.items
      .map((row) => ({ row, at: Date.parse(row?.date ?? '') }))
      .filter((candidate) => candidate.row.id !== entry.id
        && !Number.isNaN(candidate.at)
        && candidate.at < at);
    if (earlier.length === 0) return null;

    return earlier.reduce((best, candidate) => (candidate.at > best.at ? candidate : best)).row;
}

// The logbook's STRICTLY latest entry by parsed instant, or null when there is
// none -- or when two entries tie for latest. Feeds the End Counter -> item
// counter sync, which only fires on the last entry of a Captain's Log.
//
// One DESC page re-ranked in JS, previousLogbookEntry's rule: entry dates mix
// local-offset and Z spellings that don't sort lexicographically, so the
// index's own order can't be trusted for the comparison, only for putting the
// newest rows on the first page.
//
// The tie rule is previousLogbookEntry's too, applied at the top instead of
// the bottom: dates are date-only local midnights, so two entries on one day
// share an instant and neither is "after" the other. Which one is last is
// genuinely unknown, and a wrong counter write is worse than none -- so a tie
// means no latest entry at all, and a two-hop day never auto-syncs.
//
// The window IS the correctness bound: past PREVIOUS_ENTRY_PAGE rows the true
// latest can fall off the first DESC page (only when the date spellings
// disagree by more than the page covers), and this answers the page-local
// max. previousLogbookEntry accepts the same limit for the same reason.
export async function latestLogbookEntry(logbookId) {
    if (!logbookId) return null;

    const result = await client.graphql({
      query: APICompletedTasksByLogbookId,
      variables: {
        logbookId: logbookId,
        sortDirection: 'DESC',
        limit: PREVIOUS_ENTRY_PAGE,
      },
    });

    const dated = result.data.completedTasksByLogbookId.items
      .map((row) => ({ row, at: Date.parse(row?.date ?? '') }))
      .filter((candidate) => !Number.isNaN(candidate.at));
    if (dated.length === 0) return null;

    const best = dated.reduce((a, b) => (b.at > a.at ? b : a));
    const tied = dated.filter((candidate) => candidate.at === best.at);
    return tied.length > 1 ? null : best.row;
}

// Deletes all completed-task entries for a logbook.
export async function deleteCompletedTasksOfLogbookId(logbookId, { onProgress } = {}) {
  const entries = await listCompletedTasksForLogbook(logbookId);
  for (var i = 0; i < entries.length; i++) {
    await deleteCompletedTask(entries[i].id);
    onProgress?.(i + 1, entries.length);
  }
}

// Returns the most recent `limit` entries for a logbook, newest first. Fetches
// one extra so callers can tell whether more exist beyond what they show.
export async function listRecentCompletedTasksForLogbook(logbookId, limit = 6) {
  const result = await client.graphql({
    query: APICompletedTasksByLogbookId,
    variables: { logbookId: logbookId, sortDirection: 'DESC', limit: limit },
  });
  return result.data.completedTasksByLogbookId.items;
}

// Loads an item's logbooks and, for each, its most recent entries (attached as
// `logbook.completedTasks.items`). Used by the item page to render one summary
// card per logbook.
export async function listLogbooksWithRecentEntries(itemId, limit = 6) {
  const logbooks = await listLogbooks(itemId);
  await Promise.all(
    logbooks.map(async (logbook) => {
      const recent = await listRecentCompletedTasksForLogbook(logbook.id, limit);
      logbook.completedTasks = { items: recent };
    })
  );
  return logbooks;
}

// ***** Trips *****

// Local minimal trip operations. See the note at the top of this file -- we
// skip the nested `item { ... owner ... }` block the auto-generated
// operations would request (transformer-v2 owner-auth bug) and select only
// the scalar fields callers read.
export const APITripsByItemId = /* GraphQL */ `
  query TripsByItemId(
    $itemId: ID!
    $date: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelTaskPogoTripFilterInput
    $limit: Int
    $nextToken: String
  ) {
    tripsByItemId(
      itemId: $itemId
      date: $date
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        itemId
        name
        tripGroupId
        date
        from
        to
        distance
        days
        notes
        fields
        owner
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

export const APICreateTrip = /* GraphQL */ `
  mutation CreateTaskPogoTrip(
    $input: CreateTaskPogoTripInput!
    $condition: ModelTaskPogoTripConditionInput
  ) {
    createTaskPogoTrip(input: $input, condition: $condition) {
      id
      itemId
      name
      tripGroupId
      date
      from
      to
      distance
      days
      notes
      fields
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APIUpdateTrip = /* GraphQL */ `
  mutation UpdateTaskPogoTrip(
    $input: UpdateTaskPogoTripInput!
    $condition: ModelTaskPogoTripConditionInput
  ) {
    updateTaskPogoTrip(input: $input, condition: $condition) {
      id
      itemId
      name
      tripGroupId
      date
      from
      to
      distance
      days
      notes
      fields
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APIDeleteTrip = /* GraphQL */ `
  mutation DeleteTaskPogoTrip(
    $input: DeleteTaskPogoTripInput!
    $condition: ModelTaskPogoTripConditionInput
  ) {
    deleteTaskPogoTrip(input: $input, condition: $condition) {
      id
      __typename
    }
  }
`;

// A trip's `fields` blob is an AWSJSON scalar, which AppSync expects as a
// JSON *string*. Serialize plain objects before they hit the mutation
// (strings pass through untouched); an empty/absent blob is omitted. Trips
// have no optional GSI keys (itemId/date are required), so this is the only
// input massaging needed. Clone so the caller's object is never mutated.
function prepareTripInput(input) {
  const cleaned = { ...input };
  if (cleaned.fields != null && typeof cleaned.fields === 'object') {
    if (Object.keys(cleaned.fields).length === 0) {
      delete cleaned.fields;
    } else {
      cleaned.fields = JSON.stringify(cleaned.fields);
    }
  }
  return cleaned;
}

export async function createTrip(newTrip) {
  const created = await client.graphql({ query: APICreateTrip, variables: { input: prepareTripInput(newTrip) } });
  return created.data.createTaskPogoTrip;
}

export async function updateTrip(trip) {
  const updated = await client.graphql({ query: APIUpdateTrip, variables: { input: prepareTripInput(trip) } });
  return updated.data.updateTaskPogoTrip;
}

export async function deleteTrip(id) {
  await client.graphql({ query: APIDeleteTrip, variables: { input: { id: id } } });
}

// Single-trip read for the detail page (deep link / reload, i.e. any time the
// trip wasn't handed over in router state). Selects the same scalar field set
// as APITripsByItemId and skips the nested `item { ... owner ... }` block the
// auto-generated getTaskPogoTrip would request (transformer-v2 owner-auth bug;
// nobody reads it here anyway).
export const APIGetTrip = /* GraphQL */ `
  query GetTaskPogoTrip($id: ID!) {
    getTaskPogoTrip(id: $id) {
      id
      itemId
      name
      tripGroupId
      date
      from
      to
      distance
      days
      notes
      fields
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export async function retrieveTrip(id) {
  const result = await client.graphql({ query: APIGetTrip, variables: { id: id } });
  return result.data.getTaskPogoTrip;
}

// Creates a trip stub dated now, showing UI while adding/errors. Calls
// onAdded(newTrip) on success so the caller can open the editor on it --
// the same create-then-edit flow as logbook entries.
//
// `defaults` overrides fields on the created stub (the trip group page seeds a
// new leg's date/from/name from the leg before it) so they're persisted at
// create time rather than only on the first save of the editor.
export async function createTripWithUI(itemId, showText, showError, hideStatus, onAdded, defaults = {}) {
  try {
    showText('Adding...');

    const savedTrip = await createTrip({
      itemId: itemId,
      date: databaseDateTimeString(new Date()),
      ...defaults,
    });

    onAdded(savedTrip);
    hideStatus();
  }
  catch (err) {
    showError(err);
  }
}

// Returns trip legs for the Upcoming Trips card, soonest first. The card groups
// legs into trips (by Trip Name) and shows the first few groups, so it needs
// enough legs to (a) form complete groups and (b) tell whether more upcoming
// groups exist beyond what it renders.
//
// The window starts `lookbackDays` before today, not at today, so a
// multi-day trip already under way (its first leg in the recent past, later
// legs today/ahead) groups completely and stays on the card as one in-progress
// trip. `limit` is generous for the same reason -- a single trip can have many
// legs. The card still caps how many *groups* it displays.
export async function listUpcomingTripsForItem(itemId, limit = 100, lookbackDays = 30) {
  const windowStart = new Date();
  windowStart.setHours(0, 0, 0, 0);
  windowStart.setDate(windowStart.getDate() - lookbackDays);
  const result = await client.graphql({
    query: APITripsByItemId,
    variables: {
      itemId: itemId,
      sortDirection: 'ASC',
      date: { ge: databaseDateTimeString(windowStart) },
      limit: limit,
    },
  });
  return result.data.tripsByItemId.items;
}

// Lists every trip for an item (every page), newest/furthest-future first.
// Used by the item delete cascade, and by the trips calendar (which draws a
// trip as one bar and so can't work from a partial page -- see
// TripSchedule.loadAllTripsFresh).
export async function listTripsForItem(itemId) {
  return collectAllPages(
    APITripsByItemId,
    { itemId: itemId, sortDirection: 'DESC' },
    'tripsByItemId'
  );
}

// The legs of a single grouped trip, identified by a TripGroups key
// ('n:<name>' / 't:<id>'). Used by the trip-group page on a deep link / reload
// (the common path hands the group over in router state and skips this). We
// fetch every leg and group client-side rather than filter by name in the
// query: group keys are lowercased, so a server-side `name eq` couldn't match
// mixed-case spellings the way the case-insensitive grouping does. Returns the
// matching group's legs (date-ascending) or [] when the key isn't found.
export async function listTripsForGroupKey(itemId, groupKey) {
  const legs = await listTripsForItem(itemId);
  // findTripGroup rather than an === match: a bookmark, a mailed URL or a share
  // row minted before trips had ids holds 'n:<name>', and the trip it named now
  // carries an id. See TripIdentity.tripGroupsForKey for what an ambiguous one
  // resolves to.
  const group = findTripGroup(groupTrips(legs), groupKey);
  return group ? group.legs : [];
}

// Imports a parsed trip export (TripIO.parseTripExport) as a BRAND-NEW trip
// on the item -- it never touches existing legs. The trip name is deduped
// against the item's existing named groups; legs are created sequentially
// (honest progress + gentle on AppSync). On any failure the just-created
// legs are deleted so a partial import leaves no user-visible data behind,
// and the ORIGINAL error is re-thrown even if that cleanup itself fails.
// Returns { name, groupKey, legs } -- groupKey is ready for urlForTripGroup.
//
// `placeIndex` is this environment's gazetteer, AFTER the caller has ensured
// the file's places exist (places.mjs `ensurePlaces`). See importLogbook for
// why the caller does that rather than this function. The rollback below
// deliberately deletes no place row: those are global, and another user may
// have bound a waypoint to one since.
export async function importTrips(itemId, parsed, { onProgress, placeIndex } = {}) {
  const existingNames = groupTrips(await listTripsForItem(itemId))
    // Unnamed groups display a synthetic title, not a real name -- only
    // genuinely named groups ('n:' keys) count for collision.
    .filter((g) => isNamedGroupKey(g.key))
    .map((g) => g.name);

  const desired = parsed.trip.name?.trim() || '';
  // A single unnamed leg round-trips as unnamed (it stands alone under a
  // 't:<id>' key). Multiple legs MUST share a name to form one trip, so an
  // unnamed multi-leg file gets the deduped fallback name.
  const name = !desired && parsed.legs.length === 1
    ? null
    : uniqueTripName(desired, existingNames);

  // One fresh id for the whole file, never the source environment's. An import
  // "creates a BRAND-NEW trip and never touches existing legs", and carrying the
  // id in the file would let a re-import merge into the trip it came from --
  // the one thing this function promises not to do.
  const tripGroupId = name ? newTripGroupId() : null;

  const inputs = legsToCreateInputs(parsed.legs, { itemId, name, tripGroupId, placeIndex });
  const created = [];
  try {
    for (let i = 0; i < inputs.length; i++) {
      created.push(await createTrip(inputs[i]));
      onProgress?.(i + 1, inputs.length, 'legs');
    }
  } catch (err) {
    for (const leg of created) {
      try {
        await deleteTrip(leg.id);
      } catch (rollbackErr) {
        console.error(`Failed to roll back imported trip leg ${leg.id}:`, rollbackErr);
      }
    }
    throw err;
  }
  return { name, groupKey: tripGroupKey(created[0]), legs: created };
}

// Local minimal email-subscription mutations. See the note at the top of
// this file -- the auto-generated TaskPogoEmailSubscription mutations
// request a nested `item { ... owner ... }` block that triggers a
// transformer-v2 owner-auth bug on the response. Callers only read scalar
// fields off the result, so we select all of them and skip the nested
// @hasOne block.
export const APICreateEmailSubscription = /* GraphQL */ `
  mutation CreateTaskPogoEmailSubscription(
    $input: CreateTaskPogoEmailSubscriptionInput!
    $condition: ModelTaskPogoEmailSubscriptionConditionInput
  ) {
    createTaskPogoEmailSubscription(input: $input, condition: $condition) {
      id
      emailType
      email
      name
      sendHourGMT
      itemId
      sendEveryDays
      lastSentDate
      nextSendDate
      unsubscribeKey
      notifyOnTaskStateChange
      taskBucketSnapshots
      transitionLastSentDate
      transitionNextSendDate
      digestMode
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APIUpdateEmailSubscription = /* GraphQL */ `
  mutation UpdateTaskPogoEmailSubscription(
    $input: UpdateTaskPogoEmailSubscriptionInput!
    $condition: ModelTaskPogoEmailSubscriptionConditionInput
  ) {
    updateTaskPogoEmailSubscription(input: $input, condition: $condition) {
      id
      emailType
      email
      name
      sendHourGMT
      itemId
      sendEveryDays
      lastSentDate
      nextSendDate
      unsubscribeKey
      notifyOnTaskStateChange
      taskBucketSnapshots
      transitionLastSentDate
      transitionNextSendDate
      digestMode
      owner
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const APIDeleteEmailSubscription = /* GraphQL */ `
  mutation DeleteTaskPogoEmailSubscription(
    $input: DeleteTaskPogoEmailSubscriptionInput!
    $condition: ModelTaskPogoEmailSubscriptionConditionInput
  ) {
    deleteTaskPogoEmailSubscription(input: $input, condition: $condition) {
      id
      __typename
    }
  }
`;

export async function createEmailSubscription(newItem) {
  // Note: this can throw (e.g. network or security error)
  const created = await client.graphql({ query: APICreateEmailSubscription, variables: { input: newItem }});
  return created.data.createTaskPogoEmailSubscription;
}

export async function updateEmailSubscription(item) {
    // Remove any added data by the UI. `itemId` and `name` key the
    // taskPogoEmailSubscriptionsByItemIdAndName index, so they're omitted when
    // empty rather than nulled (DynamoDB rejects a NULL index key).
    let save = withoutEmptyKeys(copyOnlyKnownFields(item, emailFields), ['itemId', 'name']);

    // When the user picks "transitions only" we deliberately clear
    // `nextSendDate` so the daemon's status branch never matches. An empty
    // string wouldn't do it -- AppSync treats `null` and "omitted"
    // differently for partial updates, and only `null` unsets the field.
    if (Object.prototype.hasOwnProperty.call(item, 'nextSendDate') && !item.nextSendDate) {
      save.nextSendDate = null;
    }

    // Go do the update -- can throw!
    var updateItem = await client.graphql({ query: APIUpdateEmailSubscription, variables: {input: save }});
    return updateItem.data.updateTaskPogoEmailSubscription;
}

export async function deleteEmailSubscription(id) {
    const created = await client.graphql({ query: APIDeleteEmailSubscription, variables: { input: { id: id } }});
    return created.data.deleteTaskPogoEmailSubscription;
}


// ***** Shared Tasks ******

const APICreateSharedTasks = /* GraphQL */ `
  mutation CreateTaskPogoSharedTasks(
    $input: CreateTaskPogoSharedTasksInput!
  ) {
    createTaskPogoSharedTasks(input: $input) {
      id
      itemId
      itemName
      listType
      tasksJson
      expirationDate
      owner
      createdAt
      updatedAt
    }
  }
`;

const APIGetSharedTasks = /* GraphQL */ `
  query GetTaskPogoSharedTasks($id: ID!) {
    getTaskPogoSharedTasks(id: $id) {
      id
      itemId
      itemName
      listType
      tasksJson
      expirationDate
      owner
      createdAt
      updatedAt
    }
  }
`;

const APIDeleteSharedTasks = /* GraphQL */ `
  mutation DeleteTaskPogoSharedTasks(
    $input: DeleteTaskPogoSharedTasksInput!
  ) {
    deleteTaskPogoSharedTasks(input: $input) {
      id
    }
  }
`;

// Creates a shared tasks snapshot. entries is the array of task objects currently shown.
export async function createSharedTasks(itemId, itemName, listType, entries) {
    const tasksJson = JSON.stringify(entries.map(t => ({
        id: t.id,
        name: t.name,
        description: t.description,
        notes: t.notes,
        assignedEmail: t.assignedEmail,
        priority: t.priority,
        dueDate: t.dueDate,
        dueCounter: t.dueCounter,
        counterInterval: t.counterInterval,
        daysInterval: t.daysInterval,
        monthsInterval: t.monthsInterval,
        bucketName: t.bucketName,
        itemCounter: t.itemCounter ? { name: t.itemCounter.name, counter: t.itemCounter.counter } : null,
    })));

    const input = {
        itemId,
        itemName: itemName || '',
        listType: listType || 'all',
        tasksJson,
    };

    const result = await client.graphql({ query: APICreateSharedTasks, variables: { input } });
    return result.data.createTaskPogoSharedTasks;
}

export async function retrieveSharedTasks(id) {
    const result = await client.graphql({ query: APIGetSharedTasks, variables: { id } });
    return result.data.getTaskPogoSharedTasks;
}

export async function deleteSharedTasks(id) {
    const result = await client.graphql({ query: APIDeleteSharedTasks, variables: { input: { id } } });
    return result.data.deleteTaskPogoSharedTasks;
}


// ***** Trip Shares *****

// A public share of one trip group: a live pointer {itemId, groupKey} whose
// auto-generated id is the capability token in the share URL. These are the
// OWNER-side operations (userPool auth); the anonymous viewer never touches
// AppSync -- it goes through the share-trip Lambda's Function URL
// (src/utilities/shareTrip.mjs).

const APICreateTripShare = /* GraphQL */ `
  mutation CreateTaskPogoTripShare(
    $input: CreateTaskPogoTripShareInput!
  ) {
    createTaskPogoTripShare(input: $input) {
      id
      itemId
      groupKey
    }
  }
`;

const APITripSharesByItemId = /* GraphQL */ `
  query TripSharesByItemId($itemId: ID!, $groupKey: String!) {
    tripSharesByItemId(itemId: $itemId, groupKey: { eq: $groupKey }) {
      items {
        id
        itemId
        groupKey
      }
    }
  }
`;

// Every one of an item's shares, ids and keys only -- for the item cascade,
// which needs to find rows by ITEM rather than by group. Same shape and same
// reason as APIAllTripWeatherSubscriptionsByItemId: the query above pins the
// sort key to one group, and a cascade has no group to pin it to.
const APIAllTripSharesByItemId = /* GraphQL */ `
  query AllTripSharesByItemId($itemId: ID!, $nextToken: String) {
    tripSharesByItemId(itemId: $itemId, nextToken: $nextToken, limit: 200) {
      items {
        id
        groupKey
      }
      nextToken
    }
  }
`;

const APIUpdateTripShare = /* GraphQL */ `
  mutation UpdateTaskPogoTripShare(
    $input: UpdateTaskPogoTripShareInput!
  ) {
    updateTaskPogoTripShare(input: $input) {
      id
      itemId
      groupKey
    }
  }
`;

const APIDeleteTripShare = /* GraphQL */ `
  mutation DeleteTaskPogoTripShare(
    $input: DeleteTaskPogoTripShareInput!
  ) {
    deleteTaskPogoTripShare(input: $input) {
      id
    }
  }
`;

export async function createTripShare(itemId, groupKey) {
    const result = await client.graphql({ query: APICreateTripShare, variables: { input: { itemId, groupKey } } });
    return result.data.createTaskPogoTripShare;
}

// The owner's share for exactly this group, or null. Deterministic via the
// (itemId, groupKey) index -- owner-authed, so it only ever sees own rows.
export async function getTripShareForGroup(itemId, groupKey) {
    const result = await client.graphql({ query: APITripSharesByItemId, variables: { itemId, groupKey } });
    return result.data.tripSharesByItemId?.items?.[0] ?? null;
}

// Every share the item has published, across all of its trips.
export async function listTripSharesForItem(itemId) {
    const all = [];
    let nextToken = null;
    do {
        const result = await client.graphql({
            query: APIAllTripSharesByItemId,
            variables: { itemId, nextToken },
        });
        const page = result.data.tripSharesByItemId;
        if (page?.items) all.push(...page.items);
        nextToken = page?.nextToken ?? null;
    } while (nextToken);
    return all;
}

// Repoints a share after a rename changes the group key, keeping the link alive.
export async function updateTripShareGroupKey(id, groupKey) {
    const result = await client.graphql({ query: APIUpdateTripShare, variables: { input: { id, groupKey } } });
    return result.data.updateTaskPogoTripShare;
}

// Revoke: the link's id stops resolving, so the public page answers "gone".
export async function deleteTripShare(id) {
    const result = await client.graphql({ query: APIDeleteTripShare, variables: { input: { id } } });
    return result.data.deleteTaskPogoTripShare;
}


// ***** Logbook Trip Shares *****

// A public share of one LOGBOOK trip: a live pointer {itemId, logbookId,
// tripKey} whose auto-generated id is the capability token in the share URL.
// These are the OWNER-side operations (userPool auth); the anonymous viewer
// never touches AppSync -- it goes through the share-logbook-trip Lambda's
// Function URL (src/utilities/shareLogbookTrip.mjs).
//
// `identityId` is the Cognito Identity Pool id whose `private/<identityId>/`
// prefix holds the item's attachments. The Lambda cannot derive it, so the
// browser stores it here; see the model's comment in amplify/data/resource.ts.
//
// EVERY function below is gated by its callers on shareLogbookTripAvailable():
// a branch whose schema predates this model answers with an error, and AppSync
// rejects a WHOLE query naming a field the branch does not have.
//
// THE UPLOAD COLUMNS ARE GATED IN THE DOCUMENT ITSELF, which is the unusual part
// here and is worth the paragraph.
//
// `allowUploads` and `notifyEmail` arrive with the share-upload backend. A GraphQL
// document is static text, so a selection set naming a column the branch lacks
// fails the WHOLE operation -- and these documents are used by sharing a trip AND
// by `updateLogbookTripShareKey`, which is the RENAME and COMBINE repoint. Written
// the obvious way, a frontend that reached dev before that deploy made Share,
// Rename and Combine all fail on a shared trip with
//   Validation error of type FieldUndefined: Field 'allowUploads' in type
//   'TaskPogoLogbookTripShare' is undefined
// shown in the dialog. That is not hypothetical; it is what e2e caught.
//
// So the documents are FUNCTIONS of shareUploadAvailable(), and the mutation INPUT
// is gated the same way (an unknown input field fails identically). That is the
// pattern this app already uses everywhere else -- "the SPA gates each feature on
// the key's presence so the frontend can ship first" -- applied one level deeper,
// and it is why this change needs no particular landing order.
//
// The two id-and-key-only queries (APIAllLogbookTripShares*) deliberately do NOT
// name the columns: they exist for the repoint scan and the delete cascades,
// neither of which reads a setting, so they need no gate at all.

// THE SHARE ROW'S FIELDS, IN TWO SPELLINGS, chosen at call time by the deploy
// gate. This is the repo's stated pattern applied to a SELECTION SET rather than
// to a button: "the SPA gates each feature on the key's presence so the frontend
// can ship first" (.claude/rules/lambdas.md).
//
// It is not decoration. A GraphQL document is static text, so naming a column the
// branch lacks fails the WHOLE operation -- and these documents are used by
// sharing a trip AND by the rename and combine repoints. Without this, a frontend
// that landed before the backend deploy would make "Share", "Rename" and "Combine"
// all fail outright on a shared trip, with `Validation error of type
// FieldUndefined: Field 'allowUploads' ... is undefined` in the dialog.
//
// The duplication is two lines and buys the ordering independence the rest of this
// app's feature gates already have. Delete the `legacy` half once dev and prod
// have both deployed the columns.
const shareFields = (withUploads) => `
      id
      itemId
      logbookId
      tripKey
      identityId${withUploads ? `
      allowUploads
      notifyEmail` : ''}`;

const APICreateLogbookTripShare = (withUploads) => /* GraphQL */ `
  mutation CreateTaskPogoLogbookTripShare(
    $input: CreateTaskPogoLogbookTripShareInput!
  ) {
    createTaskPogoLogbookTripShare(input: $input) {${shareFields(withUploads)}
    }
  }
`;

// Pins the sort key to one trip: "does this trip already have a link?"
// Partitioned on logbookId rather than itemId because a tripKey is unique only
// within a logbook -- a legacy 'n:' key is nothing but a normalized name.
const APILogbookTripSharesForKey = (withUploads) => /* GraphQL */ `
  query LogbookTripSharesByLogbookId($logbookId: ID!, $tripKey: String!) {
    logbookTripSharesByLogbookId(logbookId: $logbookId, tripKey: { eq: $tripKey }) {
      items {${shareFields(withUploads)}
      }
    }
  }
`;

// Every share in one logbook, ids and keys only -- for Combine's repoint and
// for the warning it shows BEFORE the merge. The query above pins the sort key
// to one trip, and a merge spans two.
const APIAllLogbookTripSharesByLogbookId = /* GraphQL */ `
  query AllLogbookTripSharesByLogbookId($logbookId: ID!, $nextToken: String) {
    logbookTripSharesByLogbookId(logbookId: $logbookId, nextToken: $nextToken, limit: 200) {
      items {
        id
        tripKey
      }
      nextToken
    }
  }
`;

// Every share on one ITEM, for the item and logbook delete cascades, which have
// no trip to pin. Same shape and same reason as APIAllTripSharesByItemId.
const APIAllLogbookTripSharesByItemId = /* GraphQL */ `
  query AllLogbookTripSharesByItemId($itemId: ID!, $nextToken: String) {
    logbookTripSharesByItemId(itemId: $itemId, nextToken: $nextToken, limit: 200) {
      items {
        id
        tripKey
      }
      nextToken
    }
  }
`;

const APIUpdateLogbookTripShare = (withUploads) => /* GraphQL */ `
  mutation UpdateTaskPogoLogbookTripShare(
    $input: UpdateTaskPogoLogbookTripShareInput!
  ) {
    updateTaskPogoLogbookTripShare(input: $input) {${shareFields(withUploads)}
    }
  }
`;

const APIDeleteLogbookTripShare = /* GraphQL */ `
  mutation DeleteTaskPogoLogbookTripShare(
    $input: DeleteTaskPogoLogbookTripShareInput!
  ) {
    deleteTaskPogoLogbookTripShare(input: $input) {
      id
    }
  }
`;

export async function createLogbookTripShare({
    itemId,
    logbookId,
    tripKey,
    identityId,
    allowUploads,
    notifyEmail,
}) {
    const withUploads = shareUploadAvailable();
    // `identityId ?? null` rather than omitting it: an unresolvable identity must
    // still produce a share (one with no photos), not a failed one. Same for
    // `notifyEmail`: a share with nowhere to send the "someone added photos" mail
    // still accepts photos, it just says nothing about them.
    const input = { itemId, logbookId, tripKey, identityId: identityId ?? null };
    // The INPUT is gated as well as the selection set: an input field the branch
    // lacks fails the operation exactly the same way a selected one does.
    if (withUploads) {
        input.allowUploads = allowUploads ?? null;
        input.notifyEmail = notifyEmail ?? null;
    }
    const result = await client.graphql({
        query: APICreateLogbookTripShare(withUploads),
        variables: { input },
    });
    return result.data.createTaskPogoLogbookTripShare;
}

// The owner's share for exactly this trip, or null. AT MOST ONE ROW may exist
// per key -- that is ShareLinkModal's stated invariant and the reason
// repointLogbookTripShares deletes the extras rather than leaving them.
export async function getLogbookTripShareForKey(logbookId, tripKey) {
    const result = await client.graphql({
        query: APILogbookTripSharesForKey(shareUploadAvailable()),
        variables: { logbookId, tripKey },
    });
    return result.data.logbookTripSharesByLogbookId?.items?.[0] ?? null;
}

async function collectLogbookTripShares(query, variables) {
    const all = [];
    let nextToken = null;
    do {
        const result = await client.graphql({ query, variables: { ...variables, nextToken } });
        const page = result.data.logbookTripSharesByLogbookId
            ?? result.data.logbookTripSharesByItemId;
        if (page?.items) all.push(...page.items);
        nextToken = page?.nextToken ?? null;
    } while (nextToken);
    return all;
}

// Every share published from one logbook, across all of its trips.
export async function listLogbookTripSharesForLogbook(logbookId) {
    return collectLogbookTripShares(APIAllLogbookTripSharesByLogbookId, { logbookId });
}

// Every share published from one item, across all of its logbooks.
export async function listLogbookTripSharesForItem(itemId) {
    return collectLogbookTripShares(APIAllLogbookTripSharesByItemId, { itemId });
}

// Turn visitor photo uploads on or off for one published link, and keep the
// address the "someone added photos" mail goes to.
//
// A narrow verb rather than widening updateLogbookTripShareKey into a generic
// patch, matching that function's own shape: each of these mutations says exactly
// which columns it may touch, so nothing can move a trip key by accident while
// changing a setting.
//
// Gated by its callers on shareUploadAvailable(), like every other call that names
// these columns: AppSync rejects a WHOLE query naming a field the branch does not
// have, so a frontend that ships before the backend must not ask.
export async function updateLogbookTripShareUploads(id, { allowUploads, notifyEmail }) {
    if (!shareUploadAvailable()) {
        // Unreachable through the UI -- the control is gated too -- but naming
        // these columns against a branch that lacks them fails the whole mutation,
        // so refuse rather than produce that error.
        throw new Error('Adding photos is not available.');
    }
    const input = { id };
    if (allowUploads !== undefined) input.allowUploads = allowUploads;
    if (notifyEmail !== undefined) input.notifyEmail = notifyEmail ?? null;
    const result = await client.graphql({
        query: APIUpdateLogbookTripShare(shareUploadAvailable()),
        variables: { input },
    });
    return result.data.updateTaskPogoLogbookTripShare;
}

// Repoints a share after a rename or a combine moves the trip key.
export async function updateLogbookTripShareKey(id, tripKey) {
    const result = await client.graphql({
        query: APIUpdateLogbookTripShare(shareUploadAvailable()),
        variables: { input: { id, tripKey } },
    });
    return result.data.updateTaskPogoLogbookTripShare;
}

// Revoke: the link's id stops resolving, so the public page answers "gone".
export async function deleteLogbookTripShare(id) {
    const result = await client.graphql({
        query: APIDeleteLogbookTripShare,
        variables: { input: { id } },
    });
    return result.data.deleteTaskPogoLogbookTripShare;
}


// ***** Assigned Tasks Shares *****

// A public share of "every open task on this item assigned to X": a live
// pointer {itemId, assignedEmail} whose auto-generated id is the capability
// token in the share URL. These are the OWNER-side operations (userPool auth);
// the anonymous viewer never touches AppSync -- it goes through the
// share-assigned-tasks Lambda's Function URL (src/utilities/shareAssignedTasks.mjs).
//
// `assignedEmail` is the exact free-text string on the tasks. It can never be
// blank: a GSI key attribute cannot be the empty string, and an unassigned list
// is not a thing to publish.
//
// EVERY function below is gated by its callers on shareAssignedTasksAvailable():
// a branch whose schema predates this model answers with an error, and AppSync
// rejects a WHOLE query naming a field the branch does not have.
//
// THE WRITE COLUMNS ARE GATED IN THE DOCUMENT ITSELF, the same unusual move the
// logbook share makes above and for the same reason -- see the long block
// comment there, which this is the second instance of.
//
// `identityId`, `allowEdits` and `notifyEmail` arrive with the share-tasks-write
// backend. A GraphQL document is static text, so a selection set naming a column
// the branch lacks fails the WHOLE operation, and
// `getAssignedTasksShareForAssignee` runs on EVERY open of the Share dialog.
// Written the obvious way, a frontend that reached dev before that deploy would
// make Share fail outright on every assignee's list with
//   Validation error of type FieldUndefined: Field 'allowEdits' in type
//   'TaskPogoAssignedTasksShare' is undefined
// shown in the dialog -- which is exactly what e2e caught for the logbook share.
//
// So the documents are FUNCTIONS of shareTasksWriteAvailable(), and the mutation
// INPUT is gated the same way (an unknown input field fails identically).
//
// APIAllAssignedTasksSharesByItemId deliberately does NOT name them: it exists
// for the deleteItem cascade, which reads no setting, so it needs no gate.
//
// Delete the un-gated half once dev and prod have both deployed the columns.
const assignedShareFields = (withWrites) => `
        id
        itemId
        assignedEmail${withWrites ? `
        identityId
        allowEdits
        notifyEmail` : ''}`;

const APICreateAssignedTasksShare = (withWrites) => /* GraphQL */ `
  mutation CreateTaskPogoAssignedTasksShare(
    $input: CreateTaskPogoAssignedTasksShareInput!
  ) {
    createTaskPogoAssignedTasksShare(input: $input) {${assignedShareFields(withWrites)}
    }
  }
`;

// Pins the sort key to one assignee: "does this name already have a link?"
const APIAssignedTasksSharesForAssignee = (withWrites) => /* GraphQL */ `
  query AssignedTasksSharesByItemId($itemId: ID!, $assignedEmail: String!) {
    assignedTasksSharesByItemId(itemId: $itemId, assignedEmail: { eq: $assignedEmail }) {
      items {${assignedShareFields(withWrites)}
      }
    }
  }
`;

// Flipping the owner's "let this person edit" switch. Gated like the two above,
// and only ever called from behind shareTasksWriteAvailable() -- there is no
// un-gated spelling, because a branch without the column has no switch to flip.
const APIUpdateAssignedTasksShare = /* GraphQL */ `
  mutation UpdateTaskPogoAssignedTasksShare(
    $input: UpdateTaskPogoAssignedTasksShareInput!
  ) {
    updateTaskPogoAssignedTasksShare(input: $input) {
      id
      allowEdits
    }
  }
`;

// Every share on one item, ids and keys only -- for the deleteItem cascade,
// which has no assignee to pin. Same shape and same reason as
// APIAllTripSharesByItemId.
const APIAllAssignedTasksSharesByItemId = /* GraphQL */ `
  query AllAssignedTasksSharesByItemId($itemId: ID!, $nextToken: String) {
    assignedTasksSharesByItemId(itemId: $itemId, nextToken: $nextToken, limit: 200) {
      items {
        id
        assignedEmail
      }
      nextToken
    }
  }
`;

const APIDeleteAssignedTasksShare = /* GraphQL */ `
  mutation DeleteTaskPogoAssignedTasksShare(
    $input: DeleteTaskPogoAssignedTasksShareInput!
  ) {
    deleteTaskPogoAssignedTasksShare(input: $input) {
      id
    }
  }
`;

/**
 * Publish a link for one assignee's list.
 *
 * `identityId` is the Cognito Identity Pool id whose `private/<identityId>/`
 * prefix holds the item's attachments. The Lambda cannot derive it -- it is
 * minted by GetId against the user's own token -- so the browser stores it here,
 * and it is the only way a shared list can show a task's photos. OPTIONAL on
 * purpose: if it cannot be resolved, the right outcome is a share with no photos,
 * not a failed share.
 *
 * `allowEdits` is created FALSE, deliberately unlike the logbook share's
 * `allowUploads: true`. Letting a stranger rewrite and delete tasks is a bigger
 * grant than letting them add a photo, and the owner should make it in the
 * dialog, after the link exists and they can see what it covers.
 *
 * The INPUT is gated as well as the selection set: an unknown input field fails
 * the operation identically.
 */
export async function createAssignedTasksShare(itemId, assignedEmail, extras = {}) {
    if (typeof assignedEmail !== 'string' || assignedEmail.trim() === '') {
        throw new Error('Only a list assigned to someone can be shared.');
    }
    const withWrites = shareTasksWriteAvailable();
    const result = await client.graphql({
        query: APICreateAssignedTasksShare(withWrites),
        variables: {
            input: {
                itemId,
                assignedEmail,
                ...(withWrites
                    ? {
                          identityId: extras.identityId ?? null,
                          allowEdits: false,
                          notifyEmail: extras.notifyEmail ?? null,
                      }
                    : {}),
            },
        },
    });
    return result.data.createTaskPogoAssignedTasksShare;
}

// The owner's share for exactly this assignee, or null. AT MOST ONE ROW may
// exist per key -- ShareLinkModal's stated invariant.
export async function getAssignedTasksShareForAssignee(itemId, assignedEmail) {
    const result = await client.graphql({
        query: APIAssignedTasksSharesForAssignee(shareTasksWriteAvailable()),
        variables: { itemId, assignedEmail },
    });
    return result.data.assignedTasksSharesByItemId?.items?.[0] ?? null;
}

// Turn visitor editing on or off for one link. Caller-gated on
// shareTasksWriteAvailable(), like every other reader of these columns.
export async function updateAssignedTasksShareEdits(id, { allowEdits }) {
    const result = await client.graphql({
        query: APIUpdateAssignedTasksShare,
        variables: { input: { id, allowEdits } },
    });
    return result.data.updateTaskPogoAssignedTasksShare;
}

// Every share published from one item, across all of its assignees.
export async function listAssignedTasksSharesForItem(itemId) {
    const all = [];
    let nextToken = null;
    do {
        const result = await client.graphql({
            query: APIAllAssignedTasksSharesByItemId,
            variables: { itemId, nextToken },
        });
        const page = result.data.assignedTasksSharesByItemId;
        if (page?.items) all.push(...page.items);
        nextToken = page?.nextToken ?? null;
    } while (nextToken);
    return all;
}

// Revoke: the link's id stops resolving, so the public page answers "gone".
export async function deleteAssignedTasksShare(id) {
    const result = await client.graphql({
        query: APIDeleteAssignedTasksShare,
        variables: { input: { id } },
    });
    return result.data.deleteTaskPogoAssignedTasksShare;
}


// ***** Trip weather email *****

// A daily weather email for one trip: {itemId, groupKey} like a share row, plus
// the schedule. Owner-authed, so these only ever see the caller's own rows.
//
// Every scalar is written once and interpolated into all four operations: a
// field left out of one selection set is not an error, it is a value that reads
// back undefined in exactly one code path -- a modal that loses the send hour
// the moment it saves, say.
const TRIP_WEATHER_FIELDS = `
      id
      itemId
      groupKey
      tripName
      email
      timeZone
      sendHourLocal
      startDate
      stopDate
      nextSendLocalDate
      lastSentAt
      lastSendStatus
      unsubscribeKey
      owner
      createdAt
      updatedAt
`;

const APITripWeatherSubscriptionsByItemId = /* GraphQL */ `
  query TripWeatherSubscriptionsByItemId($itemId: ID!, $groupKey: String) {
    tripWeatherSubscriptionsByItemId(itemId: $itemId, groupKey: { eq: $groupKey }) {
      items {
        ${TRIP_WEATHER_FIELDS}
      }
    }
  }
`;

const APIAllTripWeatherSubscriptionsByItemId = /* GraphQL */ `
  query AllTripWeatherSubscriptionsByItemId($itemId: ID!, $nextToken: String) {
    tripWeatherSubscriptionsByItemId(itemId: $itemId, nextToken: $nextToken, limit: 200) {
      items {
        id
        groupKey
      }
      nextToken
    }
  }
`;

const APICreateTripWeatherSubscription = /* GraphQL */ `
  mutation CreateTaskPogoTripWeatherSubscription(
    $input: CreateTaskPogoTripWeatherSubscriptionInput!
  ) {
    createTaskPogoTripWeatherSubscription(input: $input) {
      ${TRIP_WEATHER_FIELDS}
    }
  }
`;

const APIUpdateTripWeatherSubscription = /* GraphQL */ `
  mutation UpdateTaskPogoTripWeatherSubscription(
    $input: UpdateTaskPogoTripWeatherSubscriptionInput!
  ) {
    updateTaskPogoTripWeatherSubscription(input: $input) {
      ${TRIP_WEATHER_FIELDS}
    }
  }
`;

const APIDeleteTripWeatherSubscription = /* GraphQL */ `
  mutation DeleteTaskPogoTripWeatherSubscription(
    $input: DeleteTaskPogoTripWeatherSubscriptionInput!
  ) {
    deleteTaskPogoTripWeatherSubscription(input: $input) {
      id
    }
  }
`;

const APISendTripWeatherEmail = /* GraphQL */ `
  mutation TaskPogoSendTripWeatherEmail($itemId: String!, $groupKey: String!, $email: String) {
    TaskPogoSendTripWeatherEmail(itemId: $itemId, groupKey: $groupKey, email: $email)
  }
`;

// The subscription for exactly this trip, or null. Deterministic via the
// (itemId, groupKey) index, exactly as getTripShareForGroup is.
export async function getTripWeatherSubscription(itemId, groupKey) {
    const result = await client.graphql({
        query: APITripWeatherSubscriptionsByItemId,
        variables: { itemId, groupKey },
    });
    return result.data.tripWeatherSubscriptionsByItemId?.items?.[0] ?? null;
}

// Every one of an item's trip weather subscriptions, ids and keys only -- for
// the delete cascades, which need to find rows by group rather than read them.
export async function listTripWeatherSubscriptionsForItem(itemId) {
    const all = [];
    let nextToken = null;
    do {
        const result = await client.graphql({
            query: APIAllTripWeatherSubscriptionsByItemId,
            variables: { itemId, nextToken },
        });
        const page = result.data.tripWeatherSubscriptionsByItemId;
        if (page?.items) all.push(...page.items);
        nextToken = page?.nextToken ?? null;
    } while (nextToken);
    return all;
}

export async function createTripWeatherSubscription(input) {
    const result = await client.graphql({
        query: APICreateTripWeatherSubscription,
        variables: { input },
    });
    return result.data.createTaskPogoTripWeatherSubscription;
}

export async function updateTripWeatherSubscription(input) {
    const result = await client.graphql({
        query: APIUpdateTripWeatherSubscription,
        variables: { input },
    });
    return result.data.updateTaskPogoTripWeatherSubscription;
}

export async function deleteTripWeatherSubscription(id) {
    const result = await client.graphql({
        query: APIDeleteTripWeatherSubscription,
        variables: { input: { id } },
    });
    return result.data.deleteTaskPogoTripWeatherSubscription;
}

// Repoints a subscription after a rename moves the group key, keeping it alive.
// Legacy 'n:' keys only -- a 'g:' key is what a rename cannot move.
export async function updateTripWeatherSubscriptionGroupKey(id, groupKey) {
    return updateTripWeatherSubscription({ id, groupKey });
}

// Send the trip's weather email right now. Works with or without a schedule;
// the Lambda verifies the caller owns the trip.
export async function sendTripWeatherEmailNow({ itemId, groupKey, email }) {
    const result = await client.graphql({
        query: APISendTripWeatherEmail,
        variables: { itemId, groupKey, email },
    });
    return result.data.TaskPogoSendTripWeatherEmail;
}


// ***** Places *****

// Shared gazetteer (TaskPogoPlace): name -> position rows read and written by
// every authenticated user. Lookup/writeback policy (normalization, curated
// protection, caching) lives in places.mjs -- these are the raw operations,
// and like everything else in this file they throw on error.

// Every scalar on the model, interpolated into all five operations below.
// Written once rather than five times on purpose: a field left out of one
// selection set is not an error, it is a row that silently reads back
// undefined in exactly one code path -- the gazetteer editor's facilities
// flickering off after a save, say. The rest of this file predates the
// realisation and repeats its selections by hand; don't copy that here.
const PlaceScalars = `
        id
        nameKey
        name
        friendlyName
        lat
        lon
        source
        curated
        anchorage
        marina
        shopping
        customs
        nexus
        fuel
        dinghyDock
        sandyBeach
        difficultLanding
        noLanding
        nearAirport
        ferry
        hiking
        dayAnchor
        restaurant
        gravelBeach
        airport
        notes
        createdAt
        updatedAt`;

export const APIPlacesByNameKey = /* GraphQL */ `
  query PlacesByNameKey($nameKey: String!, $limit: Int, $nextToken: String) {
    placesByNameKey(nameKey: $nameKey, limit: $limit, nextToken: $nextToken) {
      items {${PlaceScalars}
      }
      nextToken
    }
  }
`;

export const APIGetPlace = /* GraphQL */ `
  query GetTaskPogoPlace($id: ID!) {
    getTaskPogoPlace(id: $id) {${PlaceScalars}
    }
  }
`;

export const APICreatePlace = /* GraphQL */ `
  mutation CreateTaskPogoPlace($input: CreateTaskPogoPlaceInput!) {
    createTaskPogoPlace(input: $input) {${PlaceScalars}
    }
  }
`;

export const APIUpdatePlace = /* GraphQL */ `
  mutation UpdateTaskPogoPlace($input: UpdateTaskPogoPlaceInput!) {
    updateTaskPogoPlace(input: $input) {${PlaceScalars}
    }
  }
`;

export const APIDeletePlace = /* GraphQL */ `
  mutation DeleteTaskPogoPlace($input: DeleteTaskPogoPlaceInput!) {
    deleteTaskPogoPlace(input: $input) {
      id
    }
  }
`;

// All rows for a normalized name key. One page is plenty -- duplicates only
// arise from a rare concurrent-create race, never in bulk -- but the limit is
// sized so a curated row could not hide past the page even after implausibly
// many of them (pickPlace must SEE curated rows to prefer them).
export async function placesByNameKey(nameKey) {
    const result = await client.graphql({ query: APIPlacesByNameKey, variables: { nameKey, limit: 100 } });
    return result.data.placesByNameKey.items;
}

// One row by id, for the editor's deep links and reloads. A pure read, so it
// stays here rather than in places.mjs -- that module owns anything that
// touches a cache, and this touches none.
export async function getPlace(id) {
    const result = await client.graphql({ query: APIGetPlace, variables: { id } });
    return result.data.getTaskPogoPlace;
}

// Every write goes through the TaskPogoPlaceWrite Lambda once the branch has
// it (placeWriteAvailable): it reads the row first and lets only an admin
// touch a curated one, which model auth cannot express. `input` crosses as
// AWSJSON -- a JSON STRING on the wire -- and the answer comes back the same
// way, so both ends are (de)serialized here and the callers see the row the
// generated mutations used to hand back.
export const APIPlaceWrite = /* GraphQL */ `
  mutation TaskPogoPlaceWrite($action: String!, $input: AWSJSON!) {
    TaskPogoPlaceWrite(action: $action, input: $input)
  }
`;

async function placeWrite(action, input) {
    const result = await client.graphql({
        query: APIPlaceWrite,
        variables: { action, input: JSON.stringify(input) },
    });
    const value = result.data.TaskPogoPlaceWrite;
    return typeof value === 'string' ? JSON.parse(value) : value;
}

export async function createPlace(input) {
    if (placeWriteAvailable()) return placeWrite('create', input);
    const created = await client.graphql({ query: APICreatePlace, variables: { input } });
    return created.data.createTaskPogoPlace;
}

export async function updatePlace(input) {
    if (placeWriteAvailable()) return placeWrite('update', input);
    const updated = await client.graphql({ query: APIUpdatePlace, variables: { input } });
    return updated.data.updateTaskPogoPlace;
}

export async function deletePlace(id) {
    if (placeWriteAvailable()) {
        await placeWrite('delete', { id });
        return;
    }
    await client.graphql({ query: APIDeletePlace, variables: { input: { id } } });
}

export const APIListPlaces = /* GraphQL */ `
  query ListPlaces($limit: Int, $nextToken: String) {
    listTaskPogoPlaces(limit: $limit, nextToken: $nextToken) {
      items {${PlaceScalars}
      }
      nextToken
    }
  }
`;

// Every row in the shared gazetteer, fully paginated. Suggestion filtering
// (and its treat-a-failure-as-empty policy) lives in places.mjs.
export async function listPlaces() {
    return collectAllPages(APIListPlaces, { limit: 1000 }, 'listTaskPogoPlaces');
}


// ***** Storage ******

// Returns file keys from storage for the given bucket.
export async function storageList(bucket) {
  let files = await list({ 
    prefix: bucket,
    options: {
      accessLevel: 'private', 
      listAll: true
    }
  });

  return files.items;
}


// Returns a signed url (good for a counter) for the given bucket.
// Set validateObjectExistence to true if you want to make sure the
// file exists before returning the URL.
export async function storageGet(key, validateObjectExistence = false) {
    let options = {
        accessLevel: 'private' 
    };

    if (validateObjectExistence) {
      options.validateObjectExistence = true;
    }

    let url = await getUrl({
      key: key, 
      options: options
    });

    return url;
}

// Returns a Promise to delete a file from storage.
export async function storageDelete(key) {
    let res = await remove({
      key: key, 
      options: { 
        accessLevel: 'private' 
      }
    });

    return res;
}

// Adds a "name" property to a file stored in S3. Name is the key without the bucket at the beginning.
export function addNameToStorage(file, bucket) {
  if (bucket && file.key.startsWith(bucket)) {
    file.name = file.key.slice(bucket.length);
  } else {
    file.name = file.key;
  }
}

// For Storage list processing in to files/folders.
// If bucket is provided, sets a property 'name' on each file with the bucket removed.
// returns { files, folders }
export function processStorageList(response, bucket) {
  let files = [];
  let folders = new Set();
  response.forEach((res) => {
    if (res.size) {
      // console.log(res.key);
      let file = {...res};
      addNameToStorage(file, bucket);
      
      // files.push(file);
      if (res.key.startsWith(bucket) && res.key.slice(bucket.length).split('/').length < 2) {
        files.push(file);
      } else {
        // console.info(`Found a sub-folder file: ${res.key}`)
      }

      // sometimes files declare a folder with a / within then
      let possibleFolder = res.key
        .split('/')
        .slice(0, -1)
        .join('/');

      if (possibleFolder) {
        folders.add(possibleFolder);
      }
    } else {
      folders.add(res.key);
    }
  });
  return { files, folders };
}

// For Storage list processing in to nested folders.
export function processStorageListWithHierarchy(response) {
  const filesystem = {};
  // https://stackoverflow.com/questions/44759750/how-can-i-create-a-nested-object-representation-of-a-folder-structure
  const add = (source, target, item) => {
    const elements = source.split('/');
    const element = elements.shift();
    if (!element) return; // blank
    target[element] = target[element] || { __data: item }; // element;
    if (elements.length) {
      target[element] =
        typeof target[element] === 'object' ? target[element] : {};
      add(elements.join('/'), target[element], item);
    }
  };
  response.forEach((item) => add(item.key, filesystem, item));
  return filesystem;
}

